import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Users, PhoneCall, Clock, CheckCircle2, XCircle, Star, UserPlus, Phone, Globe, TrendingUp, User, CalendarDays, AlertTriangle, StickyNote, Eye, History, MessageSquareOff, PlusCircle, Plus, Save, Search, FileDown, FileText, BarChart2, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, ArrowLeft } from 'lucide-react';
import TrDateInput from '../components/TrDateInput';
import SmsGonderModal from '../components/SmsGonderModal';
import SmsGecmisi from '../components/SmsGecmisi';
import IysIzinModal from '../components/IysIzinModal';
import IysKayitListesi, { IysIzinDetay } from '../components/IysKayitListesi';


ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

interface Lead {
    id: number;
    fullName: string;
    phone: string;
    email?: string | null;
    source: string;
    status: number;
    assignedUserId: number;
    musteriCariID?: number | null;
    dikeySira?: number;
    lastActivityDate?: string | null;
}

interface LeadActivity {
    id: number;
    leadId: number;
    userId: number;
    note: string;
    activityDate: string;
    nextCallDate: string | null;
    isCompleted: boolean;
    lead: Lead;
}

interface LeadDurum {
    statusID: number;
    statusAdi: string;
    aktifMi: boolean;
    takip: boolean;
}

const LeadTakip: React.FC = () => {
    const { user } = useAuth();
    const [dailyTasks, setDailyTasks] = useState<LeadActivity[]>([]);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState<LeadActivity | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Modal form state
    const [formNote, setFormNote] = useState('');
    const [willCallAgain, setWillCallAgain] = useState(false);
    const [nextCallDate, setNextCallDate] = useState('');

    // Yeni Lead ekleme modal state
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);
    const [newLead, setNewLead] = useState({ fullName: '', phone: '', email: '', source: 'Telefon', note: '', nextCallDate: '', status: 1 });
    const [newLeadTekrarArama, setNewLeadTekrarArama] = useState(false);
    const [addingLead, setAddingLead] = useState(false);

    // Lead durumları (dinamik)
    const [leadDurumlar, setLeadDurumlar] = useState<LeadDurum[]>([]);
    const dragLeadId = React.useRef<number | null>(null);
    const dragOverLeadId = React.useRef<number | null>(null);
    const dragOverPosition = React.useRef<'before' | 'after'>('after');
    const [dragOverDisplay, setDragOverDisplay] = useState<{ leadId: number; position: 'before' | 'after' } | null>(null);

    // Sayfalama state
    const [leadsPage, setLeadsPage] = useState(1);
    const leadsPerPage = 10;

    // Detay modal state
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
    const [detailTab, setDetailTab] = useState<'Müşteri Bilgileri' | 'Görüşme Geçmişi' | 'Yeni Görüşme Ekle'>('Müşteri Bilgileri');
    const [notAlTab, setNotAlTab] = useState<'Görüşme Notu' | 'Müşteri Bilgileri'>('Görüşme Notu');
    const [modalLeadFullName, setModalLeadFullName] = useState('');
    const [modalLeadPhone, setModalLeadPhone] = useState('');
    const [modalLeadEmail, setModalLeadEmail] = useState('');
    const [modalLeadStatus, setModalLeadStatus] = useState<number>(1);
    const [savingModalLead, setSavingModalLead] = useState(false);
    const [leadActivities, setLeadActivities] = useState<LeadActivity[]>([]);
    const [loadingActivities, setLoadingActivities] = useState(false);

    // Detay modal düzenleme state
    const [editFullName, setEditFullName] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [showSmsModal, setShowSmsModal] = useState(false);
    const [smsRefreshKey, setSmsRefreshKey] = useState(0);
    const [editEmail, setEditEmail] = useState('');
    const [editStatus, setEditStatus] = useState<number>(1);
    const [savingLead, setSavingLead] = useState(false);

    // Yeni aktivite ekleme state
    const [newActivityNote, setNewActivityNote] = useState('');
    const [newActivityNextCallDate, setNewActivityNextCallDate] = useState('');

    // Müşteri olarak ekle state
    const [convertingLeadId, setConvertingLeadId] = useState<number | null>(null);
    const [savingActivity, setSavingActivity] = useState(false);
    const [nextCallDateOnly, setNextCallDateOnly] = useState('');
    const [savingNextCallDate, setSavingNextCallDate] = useState(false);

    // IYS state
    const [leadIysIzinler, setLeadIysIzinler] = useState<IysIzinDetay[]>([]);
    const [leadIysModal, setLeadIysModal] = useState<{iletisimTipi: 'TELEFON1'|'TELEFON2'|'EPOSTA'; iletisimVerisi: string} | null>(null);

    // Not Al modal aktivite geçmişi
    const [modalActivities, setModalActivities] = useState<LeadActivity[]>([]);
    const [loadingModalActivities, setLoadingModalActivities] = useState(false);

    // Takvim state
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
    const [calendarSummary, setCalendarSummary] = useState<{date: string, isCompleted: boolean, count: number}[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [taskFilter, setTaskFilter] = useState<'all' | 'pending' | 'completed'>('pending');

    useEffect(() => {
        fetchTasksByDate(selectedDate);
        fetchLeads();
        fetchLeadDurumlar();
        fetchKullanicilar();
    }, []);

    useEffect(() => {
        fetchCalendarSummary();
    }, [calendarMonth, calendarYear]);

    const fetchTasksByDate = async (date: string) => {
        if (!user) return;
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/LeadActivities/by-date', {
                params: { userId: user.kullaniciID, date }
            });
            setDailyTasks(response.data);
        } catch (error) {
            toast.error('Aramalar yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const fetchCalendarSummary = async () => {
        if (!user) return;
        try {
            const response = await axiosInstance.get('/api/LeadActivities/calendar-summary', {
                params: { userId: user.kullaniciID, year: calendarYear, month: calendarMonth + 1 }
            });
            setCalendarSummary(response.data);
        } catch (error) {
            console.error('Takvim verileri yüklenemedi');
        }
    };

    const handleSelectDate = (date: string) => {
        setSelectedDate(date);
        fetchTasksByDate(date);
    };

    const fetchLeads = async () => {
        try {
            const response = await axiosInstance.get('/api/Leads');
            setLeads(response.data);
        } catch (error) {
            toast.error('Potansiyel müşteriler yüklenirken hata oluştu');
        }
    };

    const fetchLeadDurumlar = async () => {
        try {
            const res = await axiosInstance.get('/api/LeadDurumlari');
            setLeadDurumlar(res.data);
        } catch {
            // durumlar yoksa hardcoded fallback çalışır
        }
    };

    const handleOpenModal = async (activity: LeadActivity) => {
        setSelectedActivity(activity);
        setFormNote('');
        setWillCallAgain(false);
        setNextCallDate('');
        setNotAlTab('Görüşme Notu');
        setModalLeadFullName(activity.lead?.fullName || '');
        setModalLeadPhone(activity.lead?.phone || '');
        setModalLeadEmail(activity.lead?.email || '');
        setModalLeadStatus(activity.lead?.status || 1);
        setShowModal(true);
        setLoadingModalActivities(true);
        try {
            const response = await axiosInstance.get('/api/LeadActivities', {
                params: { leadId: activity.leadId }
            });
            setModalActivities(response.data.filter((a: LeadActivity) => a.id !== activity.id));
        } catch (error) {
            setModalActivities([]);
        } finally {
            setLoadingModalActivities(false);
        }
    };

    const handleSaveNote = async () => {
        if (!selectedActivity || !user) return;
        setSubmitting(true);
        try {
            // Mevcut aktiviteyi tamamla
            await axiosInstance.put(`/api/LeadActivities/${selectedActivity.id}`, {
                ...selectedActivity,
                note: formNote,
                isCompleted: true
            });

            // Tekrar aranacaksa yeni aktivite oluştur
            if (willCallAgain && nextCallDate) {
                await axiosInstance.post('/api/LeadActivities', {
                    leadId: selectedActivity.leadId,
                    userId: user.kullaniciID,
                    note: '',
                    activityDate: new Date().toISOString(),
                    nextCallDate: nextCallDate,
                    isCompleted: false
                });
            }

            toast.success('Görüşme notu kaydedildi');
            setShowModal(false);
            fetchTasksByDate(selectedDate);
            fetchCalendarSummary();
        } catch (error) {
            toast.error('Not kaydedilirken hata oluştu');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddLead = async () => {
        if (!user) return;
        if (!newLead.fullName.trim() || !newLead.phone.trim()) {
            toast.warning('Ad Soyad ve Telefon alanları zorunludur');
            return;
        }
        setAddingLead(true);
        try {
            const leadResponse = await axiosInstance.post('/api/Leads', {
                fullName: newLead.fullName,
                phone: newLead.phone,
                email: newLead.email,
                source: newLead.source,
                status: newLead.status,
                assignedUserId: user.kullaniciID
            });

            // İlk arama aktivitesini oluştur
            await axiosInstance.post('/api/LeadActivities', {
                leadId: leadResponse.data.id,
                userId: user.kullaniciID,
                note: newLead.note || 'Yeni potansiyel müşteri eklendi',
                activityDate: new Date().toISOString(),
                nextCallDate: newLeadTekrarArama && newLead.nextCallDate ? new Date(newLead.nextCallDate).toISOString() : null,
                isCompleted: false
            });

            toast.success('Yeni potansiyel müşteri eklendi');
            setShowAddLeadModal(false);
            setNewLead({ fullName: '', phone: '', email: '', source: 'Telefon', note: '', nextCallDate: '', status: 1 });
            setNewLeadTekrarArama(false);
            fetchTasksByDate(selectedDate);
            fetchCalendarSummary();
            fetchLeads();
        } catch (error) {
            toast.error('Müşteri eklenirken hata oluştu');
        } finally {
            setAddingLead(false);
        }
    };

    const handleOpenDetail = async (lead: Lead) => {
        setSelectedLead(lead);
        setEditFullName(lead.fullName || '');
        setEditPhone(lead.phone || '');
        setEditEmail(lead.email || '');
        setEditStatus(lead.status || 1);
        setNewActivityNote('');
        setNewActivityNextCallDate('');
        setNextCallDateOnly('');
        setDetailTab('Müşteri Bilgileri');
        setShowDetailModal(true);
        setLoadingActivities(true);
        // IYS izinlerini yükle
        axiosInstance.get(`/api/CariIysIzinleri/lead/${lead.id}`)
            .then(r => setLeadIysIzinler(r.data))
            .catch(() => setLeadIysIzinler([]));
        try {
            const response = await axiosInstance.get('/api/LeadActivities', {
                params: { leadId: lead.id }
            });
            setLeadActivities(response.data);
        } catch (error) {
            toast.error('Aktiviteler yüklenirken hata oluştu');
        } finally {
            setLoadingActivities(false);
        }
    };

    const handleUpdateLead = async () => {
        if (!selectedLead) return;
        if (!editFullName.trim() || !editPhone.trim()) {
            toast.warning('Ad Soyad ve Telefon alanları zorunludur');
            return;
        }
        setSavingLead(true);
        try {
            await axiosInstance.put(`/api/Leads/${selectedLead.id}`, {
                ...selectedLead,
                fullName: editFullName,
                phone: editPhone,
                email: editEmail,
                status: editStatus
            });
            setSelectedLead({ ...selectedLead, fullName: editFullName, phone: editPhone, email: editEmail, status: editStatus });
            toast.success('Müşteri bilgileri güncellendi');
            fetchLeads();
        } catch (error) {
            toast.error('Müşteri bilgileri güncellenirken hata oluştu');
        } finally {
            setSavingLead(false);
        }
    };

    const handleAddActivity = async () => {
        if (!selectedLead || !user) return;
        if (!newActivityNote.trim()) {
            toast.warning('Görüşme notu zorunludur');
            return;
        }
        setSavingActivity(true);
        try {
            await axiosInstance.post('/api/LeadActivities', {
                leadId: selectedLead.id,
                userId: user.kullaniciID,
                note: newActivityNote,
                activityDate: new Date().toISOString(),
                nextCallDate: newActivityNextCallDate || null,
                isCompleted: false
            });
            // Durum değiştiyse lead güncelle
            if (editStatus !== selectedLead.status) {
                await axiosInstance.put(`/api/Leads/${selectedLead.id}`, {
                    ...selectedLead,
                    status: editStatus
                });
                setSelectedLead({ ...selectedLead, status: editStatus });
                fetchLeads();
            }
            toast.success('Yeni görüşme eklendi');
            setNewActivityNote('');
            setNewActivityNextCallDate('');
            // Aktivite listesini yenile
            const response = await axiosInstance.get('/api/LeadActivities', {
                params: { leadId: selectedLead.id }
            });
            setLeadActivities(response.data);
            fetchTasksByDate(selectedDate);
            fetchCalendarSummary();
        } catch (error) {
            toast.error('Görüşme eklenirken hata oluştu');
        } finally {
            setSavingActivity(false);
        }
    };

    const handleSaveModalLead = async () => {
        if (!selectedActivity?.lead) return;
        if (!modalLeadFullName.trim() || !modalLeadPhone.trim()) {
            toast.warning('Ad Soyad ve Telefon zorunludur');
            return;
        }
        setSavingModalLead(true);
        try {
            await axiosInstance.put(`/api/Leads/${selectedActivity.lead.id}`, {
                ...selectedActivity.lead,
                fullName: modalLeadFullName,
                phone: modalLeadPhone,
                email: modalLeadEmail,
                status: modalLeadStatus,
            });
            toast.success('Müşteri bilgileri güncellendi');
            fetchLeads();
        } catch {
            toast.error('Güncelleme sırasında hata oluştu');
        } finally {
            setSavingModalLead(false);
        }
    };

    const handleSaveNextCallDate = async () => {
        if (!selectedLead || !user) return;
        if (!nextCallDateOnly) {
            toast.warning('Lütfen bir tarih seçin');
            return;
        }
        setSavingNextCallDate(true);
        try {
            await axiosInstance.post('/api/LeadActivities', {
                leadId: selectedLead.id,
                userId: user.kullaniciID,
                note: '',
                activityDate: new Date().toISOString(),
                nextCallDate: new Date(nextCallDateOnly).toISOString(),
                isCompleted: false
            });
            toast.success('Tekrar arama tarihi eklendi');
            setNextCallDateOnly('');
            const response = await axiosInstance.get('/api/LeadActivities', { params: { leadId: selectedLead.id } });
            setLeadActivities(response.data);
            fetchTasksByDate(selectedDate);
            fetchCalendarSummary();
        } catch {
            toast.error('Tekrar arama tarihi eklenirken hata oluştu');
        } finally {
            setSavingNextCallDate(false);
        }
    };

    const handleMusteriOlarakEkle = async (lead: Lead) => {
        if (lead.musteriCariID) {
            toast.warning('Bu potansiyel müşteri zaten müşteri olarak eklenmiştir.');
            return;
        }
        setConvertingLeadId(lead.id);
        try {
            const params = user ? `?ekleyenId=${user.kullaniciID}` : '';
            await axiosInstance.post(`/api/Leads/${lead.id}/musteri-olarak-ekle${params}`);
            toast.success(`${lead.fullName} müşteri olarak eklendi. Notlar kopyalandı.`);
            fetchLeads();
            if (selectedLead && selectedLead.id === lead.id) {
                setSelectedLead({ ...selectedLead, musteriCariID: -1 }); // flag to reload
            }
        } catch (error: any) {
            const msg = error?.response?.data?.message;
            if (msg) toast.warning(msg);
            else toast.error('Müşteri olarak eklenirken hata oluştu');
        } finally {
            setConvertingLeadId(null);
        }
    };

    const maskPhone = (phone: string): string => {
        const numbers = phone.replace(/\D/g, '');
        if (numbers.length === 10) return `(0${numbers.slice(0, 3)}) *** ** ${numbers.slice(8)}`;
        return phone.replace(/\d(?=\d{2})/g, '*');
    };

    const getStatusBadge = (status: number) => {
        const statusColors = [
            { bg: '#fde8c8', color: '#b35a0a' },
            { bg: '#fef3c7', color: '#92400e' },
            { bg: '#dbeafe', color: '#1d4ed8' },
            { bg: '#d1fae5', color: '#065f46' },
            { bg: '#fee2e2', color: '#991b1b' },
            { bg: '#ede9fe', color: '#5b21b6' },
            { bg: '#fce7f3', color: '#9d174d' },
            { bg: '#ccfbf1', color: '#0f766e' },
        ];
        const durum = leadDurumlar.find(d => d.statusID === status);
        if (durum) {
            const c = statusColors[(status - 1) % statusColors.length];
            return { label: durum.statusAdi, bg: c.bg, color: c.color };
        }
        // Fallback hardcoded
        switch (status) {
            case 1: return { label: 'Yeni', bg: '#fde8c8', color: '#b35a0a' };
            case 2: return { label: 'Arandı', bg: '#fef3c7', color: '#92400e' };
            case 3: return { label: 'Beklemede', bg: '#fce7f3', color: '#9d174d' };
            case 4: return { label: 'İlgili', bg: '#d1fae5', color: '#065f46' };
            case 5: return { label: 'İlgisiz', bg: '#fee2e2', color: '#991b1b' };
            default: return { label: 'Bilinmiyor', bg: '#f1f5f9', color: '#475569' };
        }
    };

    const handleColumnDrop = async (targetStatusId: number) => {
        const sourceId = dragLeadId.current;
        const overLeadId = dragOverLeadId.current;
        const position = dragOverPosition.current;

        dragLeadId.current = null;
        dragOverLeadId.current = null;
        setDragOverDisplay(null);

        if (sourceId === null) return;
        const sourceLead = leads.find(l => l.id === sourceId);
        if (!sourceLead) return;

        const sourceStatusId = sourceLead.status ?? 1;

        // Hedef sütundan source'u çıkar, sıralı liste oluştur
        const targetCol = leads
            .filter(l => l.status === targetStatusId && l.id !== sourceId)
            .sort((a, b) => (a.dikeySira ?? 0) - (b.dikeySira ?? 0));

        // Ekleme indeksini bul
        let insertIdx = targetCol.length;
        if (overLeadId !== null) {
            const overIdx = targetCol.findIndex(l => l.id === overLeadId);
            if (overIdx >= 0) insertIdx = position === 'before' ? overIdx : overIdx + 1;
        }

        // Source'u ekle ve dikeySira yeniden numaralandır
        const updatedSource = { ...sourceLead, status: targetStatusId };
        targetCol.splice(insertIdx, 0, updatedSource);
        const newTargetCol = targetCol.map((l, i) => ({ ...l, dikeySira: i + 1 }));

        // Kaynak sütun (farklıysa) yeniden numaralandır
        const newSourceCol = sourceStatusId !== targetStatusId
            ? leads
                .filter(l => l.status === sourceStatusId && l.id !== sourceId)
                .sort((a, b) => (a.dikeySira ?? 0) - (b.dikeySira ?? 0))
                .map((l, i) => ({ ...l, dikeySira: i + 1 }))
            : [];

        // State güncelle (optimistic)
        const affected = new Map<number, Lead>();
        [...newTargetCol, ...newSourceCol].forEach(l => affected.set(l.id, l));
        setLeads(prev => prev.map(l => affected.has(l.id) ? affected.get(l.id)! : l));

        try {
            // Status değiştiyse PUT ile status + dikeySira güncelle
            if (sourceStatusId !== targetStatusId) {
                const ns = newTargetCol.find(l => l.id === sourceId);
                await axiosInstance.put(`/api/Leads/${sourceId}`, {
                    ...sourceLead,
                    status: targetStatusId,
                    dikeySira: ns?.dikeySira ?? insertIdx + 1,
                });
            }

            // Diğer dikeySira değişikliklerini toplu güncelle
            const siraUpdates: { id: number; dikeySira: number }[] = [];
            for (const l of newTargetCol) {
                if (l.id === sourceId && sourceStatusId !== targetStatusId) continue; // PUT ile zaten güncellendi
                const orig = leads.find(x => x.id === l.id);
                if (!orig || orig.dikeySira !== l.dikeySira) {
                    siraUpdates.push({ id: l.id, dikeySira: l.dikeySira! });
                }
            }
            for (const l of newSourceCol) {
                const orig = leads.find(x => x.id === l.id);
                if (!orig || orig.dikeySira !== l.dikeySira) {
                    siraUpdates.push({ id: l.id, dikeySira: l.dikeySira! });
                }
            }
            if (siraUpdates.length > 0) {
                await axiosInstance.post('/api/Leads/sira-guncelle', siraUpdates);
            }
        } catch {
            toast.error('Sıralama güncellenemedi');
            fetchLeads();
        }
    };

    const handleMoveCard = async (leadId: number, direction: 'up' | 'down') => {
        const lead = leads.find(l => l.id === leadId);
        if (!lead) return;
        const columnLeads = leads
            .filter(l => l.status === lead.status)
            .sort((a, b) => (a.dikeySira ?? 0) - (b.dikeySira ?? 0));
        const idx = columnLeads.findIndex(l => l.id === leadId);
        if (direction === 'up' && idx === 0) return;
        if (direction === 'down' && idx === columnLeads.length - 1) return;
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        const other = columnLeads[swapIdx];
        const siraA = lead.dikeySira ?? idx;
        const siraB = other.dikeySira ?? swapIdx;
        setLeads(prev => prev.map(l => {
            if (l.id === leadId) return { ...l, dikeySira: siraB };
            if (l.id === other.id) return { ...l, dikeySira: siraA };
            return l;
        }));
        try {
            await axiosInstance.post('/api/Leads/sira-guncelle', [
                { id: leadId, dikeySira: siraB },
                { id: other.id, dikeySira: siraA },
            ]);
        } catch {
            toast.error('Sıra güncellenemedi');
            setLeads(prev => prev.map(l => {
                if (l.id === leadId) return { ...l, dikeySira: siraA };
                if (l.id === other.id) return { ...l, dikeySira: siraB };
                return l;
            }));
        }
    };

    const getSourceLabel = (source: string) => {
        switch (source) {
            case 'Telefon': return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280' }} title="Telefon ile gelen müşteri">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#6b7280" width={16} height={16}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                    Telefon
                </span>
            );
            case 'Web': return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280' }} title="Web sitesinden gelen müşteri">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#6b7280" width={16} height={16}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>
                    Web
                </span>
            );
            case 'Referans': return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280' }} title="Referans ile gelen müşteri">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#6b7280" width={16} height={16}><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg>
                    Referans
                </span>
            );
            case 'Fuar': return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280' }} title="Fuardan gelen müşteri">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#6b7280" width={16} height={16}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016A3.001 3.001 0 0021 9.349m-18 0V6.999a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 6.999v2.35" /></svg>
                    Fuar
                </span>
            );
            default: return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280' }}>
                    {source || '-'}
                </span>
            );
        }
    };

    const isOverdue = (dateStr: string | null) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);
        return date < today;
    };

    // ── Arama State ─────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');

    const filteredTasks = dailyTasks.filter(task => {
        if (taskFilter === 'pending') return !task.isCompleted;
        if (taskFilter === 'completed') return task.isCompleted;
        return true;
    });

    const getSelectedDateLabel = () => {
        const d = new Date(selectedDate + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        d.setHours(0, 0, 0, 0);
        if (d.getTime() === today.getTime()) return 'Bugün';
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (d.getTime() === tomorrow.getTime()) return 'Yarın';
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (d.getTime() === yesterday.getTime()) return 'Dün';
        return new Date(selectedDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // ── Kullanıcılar State ──────────────────────────────────────
    const [kullanicilar, setKullanicilar] = useState<{ kullaniciID: number; adSoyad: string }[]>([]);

    const fetchKullanicilar = async () => {
        try {
            const res = await axiosInstance.get('/api/Kullanicilar');
            setKullanicilar(res.data.map((u: any) => ({ kullaniciID: u.kullaniciID, adSoyad: u.adSoyad })));
        } catch {
            // sessizce geç
        }
    };

    const getKullaniciAdi = (userId: number | null | undefined): string => {
        if (!userId) return '-';
        const u = kullanicilar.find(k => k.kullaniciID === userId);
        return u ? u.adSoyad : `#${userId}`;
    };

    // ── Rapor State ──────────────────────────────────────────────
    const [showRaporModal, setShowRaporModal] = useState(false);
    const [raporLoading, setRaporLoading] = useState(false);
    const [raporReady, setRaporReady] = useState(false);
    const [raporBaslangic, setRaporBaslangic] = useState(() => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    });
    const [raporBitis, setRaporBitis] = useState(() => new Date().toISOString().split('T')[0]);
    const [raporAllLeads, setRaporAllLeads] = useState<Lead[]>([]);
    const [raporAllActivities, setRaporAllActivities] = useState<LeadActivity[]>([]);
    const [raporKullanicilar, setRaporKullanicilar] = useState<{ kullaniciID: number; adSoyad: string }[]>([]);

    const fetchRaporData = async () => {
        setRaporLoading(true);
        setRaporReady(false);
        try {
            const [leadsRes, activitiesRes] = await Promise.all([
                axiosInstance.get('/api/Leads'),
                axiosInstance.get('/api/LeadActivities'),
            ]);
            setRaporAllLeads(leadsRes.data);
            setRaporAllActivities(activitiesRes.data);
            try {
                const usersRes = await axiosInstance.get('/api/Kullanicilar');
                setRaporKullanicilar(usersRes.data.map((u: any) => ({ kullaniciID: u.kullaniciID, adSoyad: u.adSoyad })));
            } catch {
                if (user) {
                    setRaporKullanicilar([{ kullaniciID: user.kullaniciID, adSoyad: user.adSoyad || 'Siz' }]);
                }
            }
            setRaporReady(true);
        } catch {
            toast.error('Rapor verisi yüklenirken hata oluştu');
        } finally {
            setRaporLoading(false);
        }
    };

    const getRaporUserName = (userId: number): string => {
        const u = raporKullanicilar.find(k => k.kullaniciID === userId);
        return u ? u.adSoyad : `Kullanıcı #${userId}`;
    };

    const getRaporData = () => {
        const filteredActivities = raporAllActivities.filter(a => {
            const ds = new Date(a.activityDate).toISOString().split('T')[0];
            return ds >= raporBaslangic && ds <= raporBitis;
        });

        // Lead'lerin ilk aktivite tarihi → tahminî "ekleme tarihi"
        const leadFirstDate: Record<number, string> = {};
        raporAllActivities.forEach(a => {
            const ds = new Date(a.activityDate).toISOString().split('T')[0];
            if (!leadFirstDate[a.leadId] || ds < leadFirstDate[a.leadId]) {
                leadFirstDate[a.leadId] = ds;
            }
        });

        const yeniLeadler = raporAllLeads.filter(l => {
            const fd = leadFirstDate[l.id];
            return fd && fd >= raporBaslangic && fd <= raporBitis;
        });

        // Günlük aktivite sayısı
        const dailyCounts: Record<string, number> = {};
        filteredActivities.forEach(a => {
            const ds = new Date(a.activityDate).toISOString().split('T')[0];
            dailyCounts[ds] = (dailyCounts[ds] || 0) + 1;
        });

        // Tüm günler seçili aralıkta
        const allDays: string[] = [];
        const cur = new Date(raporBaslangic + 'T00:00:00');
        const endD = new Date(raporBitis + 'T00:00:00');
        while (cur <= endD) {
            allDays.push(cur.toISOString().split('T')[0]);
            cur.setDate(cur.getDate() + 1);
        }

        // Kullanıcı bazlı aktivite (dönem içinde)
        const userCounts: Record<number, number> = {};
        filteredActivities.forEach(a => {
            userCounts[a.userId] = (userCounts[a.userId] || 0) + 1;
        });

        // Kullanıcı bazlı tamamlanan / bekleyen görüşme
        const userTamamlanan: Record<number, number> = {};
        const userBekleyen: Record<number, number> = {};
        filteredActivities.forEach(a => {
            if (a.isCompleted) userTamamlanan[a.userId] = (userTamamlanan[a.userId] || 0) + 1;
            else userBekleyen[a.userId] = (userBekleyen[a.userId] || 0) + 1;
        });

        // Kullanıcı bazlı atanmış lead sayısı (tüm leadler)
        const userLeadCounts: Record<number, number> = {};
        raporAllLeads.forEach(l => {
            if (l.assignedUserId) {
                userLeadCounts[l.assignedUserId] = (userLeadCounts[l.assignedUserId] || 0) + 1;
            }
        });

        // Kaynak dağılımı (yeni leadler)
        const sourceCounts: Record<string, number> = {};
        yeniLeadler.forEach(l => {
            const src = l.source || 'Diğer';
            sourceCounts[src] = (sourceCounts[src] || 0) + 1;
        });

        // Durum dağılımı (tüm leadler)
        const statusCounts: Record<number, number> = {};
        raporAllLeads.forEach(l => {
            const s = l.status || 1;
            statusCounts[s] = (statusCounts[s] || 0) + 1;
        });

        return {
            filteredActivities,
            yeniLeadler,
            dailyCounts,
            allDays,
            userCounts,
            userTamamlanan,
            userBekleyen,
            userLeadCounts,
            sourceCounts,
            statusCounts,
            tamamlanan: filteredActivities.filter(a => a.isCompleted).length,
            bekleyen: filteredActivities.filter(a => !a.isCompleted).length,
        };
    };

    const exportRaporPDF = () => {
        const { filteredActivities, yeniLeadler, userCounts, userTamamlanan, userBekleyen, userLeadCounts, tamamlanan, bekleyen } = getRaporData();
        const printDate = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const donemLabel = `${new Date(raporBaslangic + 'T00:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })} — ${new Date(raporBitis + 'T00:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;

        const userRows = Object.entries(userCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([uid, cnt], i) => `
                <tr class="${i % 2 === 1 ? 'alt' : ''}">
                    <td>${getRaporUserName(Number(uid))}</td>
                    <td class="center">
                        <span class="badge badge-dark">${cnt}</span>
                    </td>
                    <td class="center">
                        <div class="bar-wrap"><div class="bar-fill" style="width:${Math.round((cnt / Math.max(...Object.values(userCounts))) * 100)}%"></div></div>
                    </td>
                </tr>`).join('');

        // Tüm personellerin birleşik listesi (lead sahibi + görüşme yapan)
        const allUserIds = Array.from(new Set([
            ...Object.keys(userCounts).map(Number),
            ...Object.keys(userLeadCounts).map(Number),
        ]));
        const maxLeadCnt = Math.max(1, ...allUserIds.map(id => userLeadCounts[id] || 0));
        const maxActCnt = Math.max(1, ...allUserIds.map(id => userCounts[id] || 0));

        const personnelRows = allUserIds
            .sort((a, b) => ((userLeadCounts[b] || 0) + (userCounts[b] || 0)) - ((userLeadCounts[a] || 0) + (userCounts[a] || 0)))
            .map((uid, i) => {
                const leads = userLeadCounts[uid] || 0;
                const toplam = userCounts[uid] || 0;
                const tam = userTamamlanan[uid] || 0;
                const bek = userBekleyen[uid] || 0;
                const oran = toplam > 0 ? Math.round((tam / toplam) * 100) : 0;
                return `
                <tr class="${i % 2 === 1 ? 'alt' : ''}">
                    <td><strong>${getRaporUserName(uid)}</strong></td>
                    <td class="center">
                        <span class="badge badge-dark">${leads}</span>
                        <div class="bar-wrap" style="margin-top:4px"><div class="bar-fill" style="width:${Math.round((leads/maxLeadCnt)*100)}%;background:linear-gradient(90deg,#1a2340,#3b82f6)"></div></div>
                    </td>
                    <td class="center"><span class="badge badge-dark">${toplam}</span>
                        <div class="bar-wrap" style="margin-top:4px"><div class="bar-fill" style="width:${Math.round((toplam/maxActCnt)*100)}%"></div></div>
                    </td>
                    <td class="center"><span class="status-badge badge-green">${tam}</span></td>
                    <td class="center"><span class="status-badge badge-yellow">${bek}</span></td>
                    <td class="center">
                        <div style="display:flex;align-items:center;gap:6px;justify-content:center">
                            <div class="bar-wrap" style="width:60px"><div class="bar-fill" style="width:${oran}%;background:${oran>=70?'#10b981':oran>=40?'#f59e0b':'#ef4444'}"></div></div>
                            <span style="font-weight:700;font-size:10px;color:${oran>=70?'#065f46':oran>=40?'#92400e':'#991b1b'}">${oran}%</span>
                        </div>
                    </td>
                </tr>`;
            }).join('');

        const leadRows = yeniLeadler.map((l, i) => {
            const sb = getStatusBadge(l.status || 1);
            return `
                <tr class="${i % 2 === 1 ? 'alt' : ''}">
                    <td>${l.fullName || '-'}</td>
                    <td>${l.phone || '-'}</td>
                    <td>${l.source || '-'}</td>
                    <td class="center"><span class="status-badge" style="background:${sb.bg};color:${sb.color}">${sb.label}</span></td>
                </tr>`;
        }).join('');

        const activityRows = filteredActivities.map((a, i) => `
            <tr class="${i % 2 === 1 ? 'alt' : ''}">
                <td><strong>${a.lead?.fullName || '-'}</strong></td>
                <td>${getRaporUserName(a.userId)}</td>
                <td>${new Date(a.activityDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                <td class="note-cell">${a.note ? (a.note.length > 60 ? a.note.slice(0, 60) + '…' : a.note) : '<em style="color:#94a3b8">Not girilmemiş</em>'}</td>
                <td class="center"><span class="status-badge ${a.isCompleted ? 'badge-green' : 'badge-yellow'}">${a.isCompleted ? 'Tamamlandı' : 'Bekliyor'}</span></td>
            </tr>`).join('');

        const html = `<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Potansiyel Müşteri Raporu</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
    font-size: 11px;
    color: #1a2340;
    background: #fff;
    padding: 0;
  }

  /* ── Print Button ── */
  .print-btn {
    position: fixed; top: 16px; right: 16px;
    background: #e67e22; color: #fff; border: none;
    border-radius: 8px; padding: 10px 20px;
    font-size: 13px; font-weight: 700; cursor: pointer;
    box-shadow: 0 4px 12px rgba(230,126,34,0.35);
    font-family: inherit;
    display: flex; align-items: center; gap: 8px;
  }
  .print-btn:hover { background: #cf6d17; }
  @media print { .print-btn { display: none !important; } }

  /* ── Page Layout ── */
  .page {
    max-width: 900px;
    margin: 0 auto;
    padding: 36px 40px;
  }

  /* ── Header ── */
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 20px;
    margin-bottom: 24px;
    border-bottom: 3px solid #1a2340;
  }
  .header-left {}
  .header-title {
    font-size: 22px;
    font-weight: 800;
    color: #1a2340;
    letter-spacing: -0.5px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .header-title .dot {
    width: 10px; height: 10px; border-radius: 50%;
    background: #e67e22; display: inline-block;
  }
  .header-subtitle {
    font-size: 11px;
    color: #64748b;
    margin-top: 5px;
    font-weight: 500;
  }
  .header-right {
    text-align: right;
  }
  .header-right .label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #94a3b8;
    font-weight: 600;
  }
  .header-right .value {
    font-size: 12px;
    font-weight: 700;
    color: #1a2340;
    margin-top: 2px;
  }
  .header-accent-bar {
    height: 4px;
    background: linear-gradient(90deg, #1a2340 60%, #e67e22 100%);
    border-radius: 2px;
    margin-top: 4px;
    width: 120px;
    margin-left: auto;
  }

  /* ── Summary Cards ── */
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 28px;
  }
  .summary-card {
    border-radius: 12px;
    padding: 14px 16px;
    position: relative;
    overflow: hidden;
  }
  .summary-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
  }
  .card-navy { background: #f0f4ff; }
  .card-navy::before { background: #1a2340; }
  .card-orange { background: #fff7ed; }
  .card-orange::before { background: #e67e22; }
  .card-green { background: #f0fdf4; }
  .card-green::before { background: #10b981; }
  .card-slate { background: #f8fafc; }
  .card-slate::before { background: #64748b; }
  .card-label {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #64748b;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .card-value {
    font-size: 26px;
    font-weight: 800;
    line-height: 1;
  }
  .card-navy .card-value { color: #1a2340; }
  .card-orange .card-value { color: #e67e22; }
  .card-green .card-value { color: #10b981; }
  .card-slate .card-value { color: #475569; }

  /* ── Section ── */
  .section { margin-bottom: 28px; }
  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 2px solid #f1f5f9;
  }
  .section-icon {
    width: 22px; height: 22px;
    border-radius: 6px;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 800; color: #fff;
    flex-shrink: 0;
  }
  .icon-navy { background: #1a2340; }
  .icon-orange { background: #e67e22; }
  .icon-green { background: #10b981; }
  .section-title {
    font-size: 13px;
    font-weight: 700;
    color: #1a2340;
  }
  .section-count {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    background: #f1f5f9;
    color: #475569;
    padding: 2px 8px;
    border-radius: 20px;
  }

  /* ── Tables ── */
  table { width: 100%; border-collapse: collapse; }
  thead tr {
    background: #1a2340;
    color: #fff;
  }
  thead th {
    padding: 9px 12px;
    text-align: left;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
  thead th.center { text-align: center; }
  tbody tr td {
    padding: 8px 12px;
    font-size: 10.5px;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }
  tbody tr td.center { text-align: center; }
  tbody tr.alt td { background: #f8fafc; }
  tbody tr:last-child td { border-bottom: none; }
  .table-wrap {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
  }

  /* ── Özet Tablo (2 sütun yan yana) ── */
  .summary-table-wrap {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 28px;
  }

  /* ── Badges ── */
  .badge { display: inline-block; padding: 2px 8px; border-radius: 5px; font-weight: 700; font-size: 10px; }
  .badge-dark { background: #1a2340; color: #fff; }
  .status-badge { display: inline-block; padding: 2px 9px; border-radius: 5px; font-size: 9.5px; font-weight: 700; }
  .badge-green { background: #dcfce7; color: #166534; }
  .badge-yellow { background: #fef3c7; color: #92400e; }

  /* ── Bar Chart ── */
  .bar-wrap { background: #f1f5f9; border-radius: 4px; height: 8px; min-width: 60px; overflow: hidden; }
  .bar-fill { height: 100%; background: linear-gradient(90deg, #1a2340, #e67e22); border-radius: 4px; }

  /* ── Note cell ── */
  .note-cell { max-width: 220px; word-break: break-word; }

  /* ── Footer ── */
  .footer {
    margin-top: 32px;
    padding-top: 14px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 9.5px;
    color: #94a3b8;
  }
  .footer strong { color: #1a2340; }

  /* ── Print ── */
  @media print {
    body { padding: 0; }
    .page { padding: 20px 24px; max-width: 100%; }
    .section { page-break-inside: avoid; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; }
  }
</style>
</head>
<body>
<button class="print-btn" onclick="window.print()">&#128438; PDF Olarak Kaydet</button>

<div class="page">

  <!-- HEADER -->
  <div class="header">
    <div class="header-left">
      <div class="header-title">
        <span class="dot"></span>
        Potansiyel Müşteri Raporu
      </div>
      <div class="header-subtitle">Dönem: ${donemLabel}</div>
    </div>
    <div class="header-right">
      <div class="label">Oluşturma Tarihi</div>
      <div class="value">${printDate}</div>
      <div class="header-accent-bar"></div>
    </div>
  </div>

  <!-- ÖZET KARTLAR -->
  <div class="summary-grid">
    <div class="summary-card card-navy">
      <div class="card-label">Toplam Lead</div>
      <div class="card-value">${raporAllLeads.length}</div>
    </div>
    <div class="summary-card card-orange">
      <div class="card-label">Dönemde Yeni</div>
      <div class="card-value">${yeniLeadler.length}</div>
    </div>
    <div class="summary-card card-green">
      <div class="card-label">Tamamlanan</div>
      <div class="card-value">${tamamlanan}</div>
    </div>
    <div class="summary-card card-slate">
      <div class="card-label">Bekleyen</div>
      <div class="card-value">${bekleyen}</div>
    </div>
  </div>

  <!-- ÖZET + KULLANICI YAN YANA -->
  <div class="summary-table-wrap">
    <!-- Özet Tablo -->
    <div class="section" style="margin-bottom:0">
      <div class="section-header">
        <div class="section-icon icon-orange">&#9776;</div>
        <div class="section-title">Dönem Özeti</div>
      </div>
      <div class="table-wrap">
        <table>
          <thead><tr><th>Metrik</th><th class="center">Değer</th></tr></thead>
          <tbody>
            <tr><td>Toplam Lead</td><td class="center"><span class="badge badge-dark">${raporAllLeads.length}</span></td></tr>
            <tr class="alt"><td>Dönemde Yeni Lead</td><td class="center"><span class="badge badge-dark">${yeniLeadler.length}</span></td></tr>
            <tr><td>Toplam Görüşme</td><td class="center"><span class="badge badge-dark">${filteredActivities.length}</span></td></tr>
            <tr class="alt"><td>Tamamlanan Görüşme</td><td class="center"><span class="status-badge badge-green">${tamamlanan}</span></td></tr>
            <tr><td>Bekleyen Görüşme</td><td class="center"><span class="status-badge badge-yellow">${bekleyen}</span></td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Kullanıcı Tablosu -->
    ${Object.keys(userCounts).length > 0 ? `
    <div class="section" style="margin-bottom:0">
      <div class="section-header">
        <div class="section-icon icon-navy">&#128100;</div>
        <div class="section-title">Kullanıcı Bazlı Görüşme</div>
        <span class="section-count">${Object.keys(userCounts).length} kullanıcı</span>
      </div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Kullanıcı</th>
              <th class="center">Sayı</th>
              <th>Dağılım</th>
            </tr>
          </thead>
          <tbody>${userRows}</tbody>
        </table>
      </div>
    </div>` : ''}
  </div>

  <!-- PERSONEL İSTATİSTİKLERİ -->
  ${allUserIds.length > 0 ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon icon-navy">&#128202;</div>
      <div class="section-title">Personel Performans İstatistikleri</div>
      <span class="section-count">${allUserIds.length} personel</span>
    </div>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Personel</th>
            <th class="center">Atanmış Lead</th>
            <th class="center">Toplam Görüşme</th>
            <th class="center">Tamamlanan</th>
            <th class="center">Bekleyen</th>
            <th class="center">Tamamlanma Oranı</th>
          </tr>
        </thead>
        <tbody>${personnelRows}</tbody>
      </table>
    </div>
  </div>` : ''}

  <!-- YENİ EKLENEN MÜŞTERİLER -->
  ${yeniLeadler.length > 0 ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon icon-green">+</div>
      <div class="section-title">Dönemde Eklenen Müşteriler</div>
      <span class="section-count">${yeniLeadler.length} kayıt</span>
    </div>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Ad Soyad</th>
            <th>Telefon</th>
            <th>Kaynak</th>
            <th class="center">Durum</th>
          </tr>
        </thead>
        <tbody>${leadRows}</tbody>
      </table>
    </div>
  </div>` : ''}

  <!-- GÖRÜŞME LİSTESİ -->
  ${filteredActivities.length > 0 ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon icon-navy">&#9742;</div>
      <div class="section-title">Görüşme Listesi</div>
      <span class="section-count">${filteredActivities.length} görüşme</span>
    </div>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Müşteri</th>
            <th>Kullanıcı</th>
            <th>Tarih</th>
            <th>Not</th>
            <th class="center">Durum</th>
          </tr>
        </thead>
        <tbody>${activityRows}</tbody>
      </table>
    </div>
  </div>` : ''}

  <!-- FOOTER -->
  <div class="footer">
    <div>YapiCRM &nbsp;|&nbsp; <strong>Potansiyel Müşteri Yönetimi</strong></div>
    <div>Dönem: <strong>${donemLabel}</strong> &nbsp;|&nbsp; Oluşturma: <strong>${printDate}</strong></div>
  </div>

</div>
<script>
  window.onload = function() { setTimeout(function() { window.print(); }, 400); };
</script>
</body>
</html>`;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(html);
            printWindow.document.close();
        }
    };
    // ── /Rapor ───────────────────────────────────────────────────

    const renderCalendar = () => {
        const firstDay = new Date(calendarYear, calendarMonth, 1);
        const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
        const startDow = (firstDay.getDay() + 6) % 7; // Monday=0
        const daysInMonth = lastDay.getDate();
        const weeks: (number | null)[][] = [];
        let currentWeek: (number | null)[] = [];
        for (let i = 0; i < startDow; i++) currentWeek.push(null);
        for (let d = 1; d <= daysInMonth; d++) {
            currentWeek.push(d);
            if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; }
        }
        if (currentWeek.length > 0) {
            while (currentWeek.length < 7) currentWeek.push(null);
            weeks.push(currentWeek);
        }

        const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
        const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
        const todayStr = new Date().toISOString().split('T')[0];

        const getCountForDay = (day: number) => {
            const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const entries = calendarSummary.filter(s => s.date === dateStr);
            const pending = entries.filter(s => !s.isCompleted).reduce((sum, s) => sum + s.count, 0);
            const completed = entries.filter(s => s.isCompleted).reduce((sum, s) => sum + s.count, 0);
            return { pending, completed, total: pending + completed };
        };

        return (
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <button onClick={() => {
                        if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(calendarYear - 1); }
                        else setCalendarMonth(calendarMonth - 1);
                    }} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
                        <ChevronLeft size={16} />
                    </button>
                    <span style={{ fontWeight: 700, color: '#1a2340', fontSize: '1rem' }}>
                        {monthNames[calendarMonth]} {calendarYear}
                    </span>
                    <button onClick={() => {
                        if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(calendarYear + 1); }
                        else setCalendarMonth(calendarMonth + 1);
                    }} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
                        <ChevronRight size={16} />
                    </button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            {dayNames.map(dn => (
                                <th key={dn} style={{ padding: '6px 4px', fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textAlign: 'center' }}>{dn}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {weeks.map((week, wi) => (
                            <tr key={wi}>
                                {week.map((day, di) => {
                                    if (!day) return <td key={di} style={{ padding: '4px' }}></td>;
                                    const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                    const isSelected = dateStr === selectedDate;
                                    const isToday = dateStr === todayStr;
                                    const counts = getCountForDay(day);
                                    return (
                                        <td key={di} style={{ padding: '2px', textAlign: 'center' }}>
                                            <div
                                                onClick={() => { handleSelectDate(dateStr); setShowCalendar(false); }}
                                                style={{
                                                    padding: '6px 2px', borderRadius: '8px', cursor: 'pointer',
                                                    background: isSelected ? '#e67e22' : isToday ? '#fef6eb' : 'transparent',
                                                    color: isSelected ? '#fff' : '#1a2340',
                                                    fontWeight: isToday || isSelected ? 700 : 400,
                                                    fontSize: '0.8rem',
                                                    border: isToday && !isSelected ? '1px solid #e67e22' : '1px solid transparent',
                                                    transition: 'all 0.15s',
                                                    position: 'relative'
                                                }}
                                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f1f5f9'; }}
                                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isToday ? '#fef6eb' : 'transparent'; }}
                                            >
                                                {day}
                                                {counts.total > 0 && (
                                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', marginTop: '2px' }}>
                                                        {counts.pending > 0 && (
                                                            <span style={{
                                                                fontSize: '0.55rem', fontWeight: 700,
                                                                background: isSelected ? 'rgba(255,255,255,0.3)' : '#fef3c7',
                                                                color: isSelected ? '#fff' : '#92400e',
                                                                borderRadius: '4px', padding: '0px 3px', lineHeight: '1.4'
                                                            }}>{counts.pending}</span>
                                                        )}
                                                        {counts.completed > 0 && (
                                                            <span style={{
                                                                fontSize: '0.55rem', fontWeight: 700,
                                                                background: isSelected ? 'rgba(255,255,255,0.3)' : '#dcfce7',
                                                                color: isSelected ? '#fff' : '#166534',
                                                                borderRadius: '4px', padding: '0px 3px', lineHeight: '1.4'
                                                            }}>{counts.completed}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '10px', fontSize: '0.7rem', color: '#64748b' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#fef3c7', display: 'inline-block' }}></span>
                        Bekliyor
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#dcfce7', display: 'inline-block' }}></span>
                        Tamamlandı
                    </span>
                </div>
            </div>
        );
    };

    return (
        <>
        <style>{`
          @media (max-width: 768px) {
            .lt-page { padding: 14px !important; }
            .lt-header { flex-wrap: wrap !important; gap: 12px !important; }
            .lt-header h2 { font-size: 1.15rem !important; }
            .lt-header-btn { width: 100% !important; justify-content: center !important; }
            .lt-card { padding: 16px !important; }
            .lt-card-head { flex-wrap: wrap !important; gap: 10px !important; }
            .lt-filters { flex-wrap: wrap !important; width: 100% !important; justify-content: flex-start !important; }
            .lt-task-row {
              flex-wrap: wrap !important; gap: 8px !important;
              padding: 14px !important;
            }
            .lt-task-row > div[style*="minWidth"],
            .lt-task-row > div { min-width: 0 !important; flex-shrink: 1 !important; }
            .lt-task-name { min-width: 0 !important; flex: 1 1 auto !important; }
            .lt-task-phone { min-width: 0 !important; }
            .lt-task-source { min-width: 0 !important; }
            .lt-task-date { min-width: 0 !important; flex: 1 1 100% !important; }
            .lt-task-btn { width: 100% !important; justify-content: center !important; }
            .lt-task-spacer { display: none !important; }
            .lt-calendar-popup { width: 280px !important; right: -60px !important; }
            .lt-modal-inner { padding: 20px !important; margin: 10px !important; max-width: calc(100vw - 20px) !important; }
            .lt-modal-grid { grid-template-columns: 1fr !important; }
            .lt-pagination { flex-wrap: wrap !important; gap: 8px !important; }
            .lt-pagination > div { flex-wrap: wrap !important; }
          }
        `}</style>
        <div className="lt-page" style={{ padding: '24px', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif', position: 'relative' }}>
            {/* Sayfa Başlığı */}
            <div className="lt-header" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                marginBottom: '32px', gap: '16px', flexWrap: 'wrap'
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <h1 style={{ margin: 0, fontWeight: 700, color: '#111827', fontSize: '1.5rem', letterSpacing: '-0.025em', lineHeight: 1.2 }}>
                        Potansiyel Müşteriler
                    </h1>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                        Satış hunisindeki aday müşterilerinizi ve süreçleri yönetin.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                    onClick={() => setShowRaporModal(true)}
                    style={{
                        background: '#fff',
                        color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px',
                        padding: '7px 14px', fontWeight: 500, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '6px',
                        fontSize: '0.875rem',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#d1d5db'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                    Rapor
                </button>
                <button
                    className="lt-header-btn"
                    onClick={() => setShowAddLeadModal(true)}
                    style={{
                        background: '#cd6906',
                        color: '#fff', border: 'none', borderRadius: '8px',
                        padding: '7px 16px', fontWeight: 600, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '6px',
                        fontSize: '0.875rem',
                        boxShadow: '0 1px 3px rgba(205,105,6,0.35)',
                        transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#b85e05'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                    Yeni Aday Ekle
                </button>
                </div>
            </div>

            {/* Yapılacak Aramalar */}
            <div className="lt-card" style={{
                background: '#fff', borderRadius: '16px', padding: '24px',
                border: '1px solid #e2e8f0', marginBottom: '28px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}>
                <div className="lt-card-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '40px', height: '40px', borderRadius: '10px',
                            background: '#fffbeb',
                            border: '1px solid #fde68a',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <PhoneCall size={18} color="#d97706" />
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontWeight: 700, color: '#1a2340', fontSize: '1.1rem' }}>
                                {getSelectedDateLabel()} — Aramalar
                            </h4>
                            <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                                {filteredTasks.length} adet arama
                            </span>
                        </div>
                    </div>
                    <div className="lt-filters" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {/* Filtre */}
                        <div style={{ display: 'flex', gap: '6px' }}>
                            {([
                                { key: 'pending' as const, label: 'Bekliyor' },
                                { key: 'completed' as const, label: 'Tamamlandı' },
                                { key: 'all' as const, label: 'Tümü' }
                            ]).map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => setTaskFilter(f.key)}
                                    style={{
                                        padding: '6px 16px', border: 'none', cursor: 'pointer',
                                        fontSize: '0.78rem', fontWeight: 600, borderRadius: '100px',
                                        background: taskFilter === f.key ? '#e67e22' : '#f3f4f6',
                                        color: taskFilter === f.key ? '#fff' : '#6b7280',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        {/* Takvim butonu */}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => { setShowCalendar(!showCalendar); setCalendarMonth(new Date(selectedDate + 'T00:00:00').getMonth()); setCalendarYear(new Date(selectedDate + 'T00:00:00').getFullYear()); }}
                                style={{
                                    background: showCalendar ? '#e67e22' : '#fff',
                                    color: showCalendar ? '#fff' : '#e67e22',
                                    border: '1px solid #e67e22',
                                    borderRadius: '8px', padding: '8px 16px',
                                    fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <CalendarDays size={15} /> Takvim
                            </button>
                            {showCalendar && (
                                <div className="lt-calendar-popup" style={{
                                    position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                                    background: '#fff', borderRadius: '14px', padding: '18px',
                                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0',
                                    zIndex: 100, width: '320px'
                                }}>
                                    {renderCalendar()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                        <Loader2 size={32} color="#cbd5e1" style={{ marginBottom: '12px', animation: 'spin 1s linear infinite' }} />
                        <p>Yükleniyor...</p>
                    </div>
                ) : filteredTasks.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                        Bu tarihte arama bulunmuyor
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filteredTasks.map(task => {
                            const overdue = isOverdue(task.nextCallDate);
                            const statusInfo = task.lead ? getStatusBadge(task.lead.status || 1) : getStatusBadge(1);
                            return (
                                <div key={task.id} className="lt-task-row" style={{
                                    background: task.isCompleted
                                        ? 'linear-gradient(135deg, #f0fdf4, #ecfdf5)'
                                        : overdue
                                            ? 'linear-gradient(135deg, #fef2f2, #fff1f2)'
                                            : 'linear-gradient(135deg, #fef7ed, #fef6eb)',
                                    borderRadius: '14px',
                                    padding: '16px 20px',
                                    border: task.isCompleted ? '1px solid #bbf7d0' : overdue ? '1px solid #fecaca' : '1px solid #fcd9a8',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    cursor: 'default',
                                    display: 'flex', alignItems: 'center', gap: '16px',
                                    opacity: task.isCompleted ? 0.8 : 1
                                }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    {/* Avatar */}
                                    <div style={{
                                        width: '44px', height: '44px', borderRadius: '12px',
                                        background: task.isCompleted
                                            ? 'linear-gradient(135deg, #10b981, #059669)'
                                            : overdue
                                                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                                                : 'linear-gradient(135deg, #e67e22, #cf6d17)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <User size={18} color="#fff" />
                                    </div>

                                    {/* Ad Soyad & Durum */}
                                    <div className="lt-task-name" style={{ minWidth: '140px', flexShrink: 0 }}>
                                        <h5 style={{ margin: 0, fontWeight: 700, color: '#1a2340', fontSize: '0.95rem' }}>
                                            {task.lead?.fullName || 'İsimsiz'}
                                        </h5>
                                        <span style={{
                                            display: 'inline-block', padding: '2px 8px', borderRadius: '6px',
                                            fontSize: '0.7rem', fontWeight: 600,
                                            background: statusInfo.bg, color: statusInfo.color, marginTop: '4px'
                                        }}>
                                            {statusInfo.label}
                                        </span>
                                    </div>

                                    {/* Telefon */}
                                    <div className="lt-task-phone" style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '120px', flexShrink: 0 }}>
                                        <Phone size={13} color="#cd6906" />
                                        <span style={{ color: '#334155', fontWeight: 600, fontSize: '0.9rem' }}>
                                            {task.lead?.phone ? maskPhone(task.lead.phone) : '-'}
                                        </span>
                                    </div>

                                    {/* Kaynak */}
                                    <div className="lt-task-source" style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '110px', flexShrink: 0 }}>
                                        {getSourceLabel(task.lead?.source || '')}
                                    </div>

                                    {/* Tarih */}
                                    <div className="lt-task-date" style={{
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        padding: '6px 12px',
                                        background: overdue ? 'rgba(239,68,68,0.08)' : 'rgba(205,105,6,0.08)',
                                        borderRadius: '8px', minWidth: '130px', flexShrink: 0
                                    }}>
                                        <CalendarDays size={13} color={overdue ? '#ef4444' : '#cd6906'} />
                                        <span style={{
                                            color: overdue ? '#dc2626' : '#b35a0a',
                                            fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap'
                                        }}>
                                            {task.nextCallDate
                                                ? new Date(task.nextCallDate).toLocaleDateString('tr-TR', {
                                                    day: '2-digit', month: '2-digit', year: 'numeric'
                                                })
                                                : '-'
                                            }
                                        </span>
                                    </div>

                                    {overdue && !task.isCompleted && (
                                        <span style={{
                                            background: '#fef2f2', color: '#dc2626', fontSize: '0.7rem',
                                            fontWeight: 600, padding: '4px 8px', borderRadius: '6px',
                                            border: '1px solid #fecaca', whiteSpace: 'nowrap', flexShrink: 0
                                        }}>
                                            <AlertTriangle size={11} style={{ marginRight: '3px' }} />
                                            Gecikmiş
                                        </span>
                                    )}

                                    {task.isCompleted && (
                                        <span style={{
                                            background: '#dcfce7', color: '#166534', fontSize: '0.7rem',
                                            fontWeight: 600, padding: '4px 8px', borderRadius: '6px',
                                            border: '1px solid #bbf7d0', whiteSpace: 'nowrap', flexShrink: 0
                                        }}>
                                            <CheckCircle2 size={11} style={{ marginRight: '3px' }} />
                                            Tamamlandı
                                        </span>
                                    )}

                                    {/* Spacer */}
                                    <div className="lt-task-spacer" style={{ flex: 1 }}></div>

                                    {/* Ara / Not Al Butonu */}
                                    {!task.isCompleted && (
                                        <button
                                            className="lt-task-btn"
                                            onClick={() => handleOpenModal(task)}
                                            style={{
                                                padding: '10px 20px',
                                                background: overdue
                                                    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                                                    : 'linear-gradient(135deg, #10b981, #059669)',
                                                color: '#fff', border: 'none', borderRadius: '10px',
                                                fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
                                                display: 'flex', alignItems: 'center', gap: '8px',
                                                transition: 'opacity 0.2s', whiteSpace: 'nowrap', flexShrink: 0
                                            }}
                                            onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
                                            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                                        >
                                            <PhoneCall size={14} />
                                            Ara / Not Al
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── İstatistik Widget'ları ── */}
            {(() => {
                const totalLeads = leads.length;
                const kanbanLeads = leads.filter(l => leadDurumlar.find(d => d.statusID === l.status && d.takip)).length;
                const todayPending = dailyTasks.filter(t => !t.isCompleted).length;
                const todayCompleted = dailyTasks.filter(t => t.isCompleted).length;
                const overdue = dailyTasks.filter(t => !t.isCompleted && isOverdue(t.nextCallDate)).length;
                const widgets = [
                    { label: 'Toplam Aday', value: totalLeads, icon: <Users size={18} color="#6366f1" />, bg: '#eef2ff', border: '#e0e7ff', valColor: '#4338ca' },
                    { label: 'Takip Panoda', value: kanbanLeads, icon: <TrendingUp size={18} color="#0891b2" />, bg: '#ecfeff', border: '#cffafe', valColor: '#0e7490' },
                    { label: 'Bugün Bekleyen', value: todayPending, icon: <Clock size={18} color="#d97706" />, bg: '#fffbeb', border: '#fde68a', valColor: '#b45309' },
                    { label: 'Bugün Tamamlanan', value: todayCompleted, icon: <CheckCircle2 size={18} color="#059669" />, bg: '#f0fdf4', border: '#bbf7d0', valColor: '#047857' },
                    { label: 'Gecikmiş', value: overdue, icon: <AlertTriangle size={18} color="#dc2626" />, bg: '#fef2f2', border: '#fecaca', valColor: '#b91c1c' },
                ];
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
                        {widgets.map(w => (
                            <div key={w.label} style={{
                                background: w.bg, border: `1px solid ${w.border}`,
                                borderRadius: 12, padding: '14px 16px',
                                display: 'flex', alignItems: 'center', gap: 12,
                            }}>
                                <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 9, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                    {w.icon}
                                </div>
                                <div>
                                    <div style={{ fontSize: '1.35rem', fontWeight: 800, color: w.valColor, lineHeight: 1 }}>{w.value}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 3, whiteSpace: 'nowrap' }}>{w.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                );
            })()}

            {/* ── Arama Çubuğu ── */}
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
                    <Search size={16} color="#9ca3af" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                    <input
                        type="text"
                        placeholder="Ad Soyad veya Telefon ile ara…"
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setLeadsPage(1); }}
                        style={{
                            width: '100%', padding: '8px 12px 8px 36px',
                            border: '1px solid #e5e7eb', borderRadius: 9,
                            fontSize: '0.875rem', outline: 'none',
                            background: '#fff', color: '#111827',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.15s',
                        }}
                        onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                        onBlur={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
                    />
                </div>
                {searchQuery && (
                    <button
                        onClick={() => { setSearchQuery(''); setLeadsPage(1); }}
                        style={{
                            background: '#f3f4f6', border: 'none', borderRadius: 8,
                            padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                            fontSize: '0.8rem', color: '#6b7280', fontWeight: 500,
                        }}
                    >
                        <X size={14} /> Temizle
                    </button>
                )}
                {searchQuery && (
                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {leads.filter(l => {
                            const q = searchQuery.toLowerCase();
                            return (l.fullName || '').toLowerCase().includes(q) || (l.phone || '').includes(q);
                        }).length} sonuç
                    </span>
                )}
            </div>

            {/* Kanban Board — Takip=true olan durumlar */}
            {(() => {
                const kanbanDurumlar = leadDurumlar.filter(d => d.aktifMi && d.takip);
                if (kanbanDurumlar.length === 0) return null;
                const kanbanColors = [
                    { header: '#3b82f6', headerLight: '#eff6ff', dot: '#93c5fd' },
                    { header: '#8b5cf6', headerLight: '#f5f3ff', dot: '#c4b5fd' },
                    { header: '#10b981', headerLight: '#f0fdf4', dot: '#6ee7b7' },
                    { header: '#f59e0b', headerLight: '#fffbeb', dot: '#fcd34d' },
                    { header: '#ef4444', headerLight: '#fef2f2', dot: '#fca5a5' },
                    { header: '#06b6d4', headerLight: '#ecfeff', dot: '#67e8f9' },
                    { header: '#ec4899', headerLight: '#fdf2f8', dot: '#f9a8d4' },
                    { header: '#e67e22', headerLight: '#fff7ed', dot: '#fbbf24' },
                ];
                const kanbanColumnIcons = [
                    <UserPlus size={14} />,
                    <PhoneCall size={14} />,
                    <Clock size={14} />,
                    <Star size={14} />,
                    <XCircle size={14} />,
                    <TrendingUp size={14} />,
                    <CheckCircle2 size={14} />,
                    <Users size={14} />,
                ];
                return (
                    <div style={{ marginBottom: 28 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                            <div>
                                <h4 style={{ margin: 0, fontWeight: 700, color: '#111827', fontSize: '1rem', letterSpacing: '-0.015em' }}>
                                    Takip Panosu
                                </h4>
                                <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                                    Sürükle-bırak ile durum değiştirin
                                </span>
                            </div>
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                            gap: 16,
                        }}>
                            {kanbanDurumlar.map((durum, idx) => {
                                const col = kanbanColors[idx % kanbanColors.length];
                                const columnLeads = leads
                                    .filter(l => {
                                        if (l.status !== durum.statusID) return false;
                                        if (!searchQuery) return true;
                                        const q = searchQuery.toLowerCase();
                                        return (l.fullName || '').toLowerCase().includes(q) || (l.phone || '').includes(q);
                                    })
                                    .sort((a, b) => (a.dikeySira ?? 0) - (b.dikeySira ?? 0));
                                return (
                                    <div
                                        key={durum.statusID}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            // Kart üzerinde değilsek (stopPropagation olmadığında buraya gelir)
                                            dragOverLeadId.current = null;
                                            setDragOverDisplay(null);
                                        }}
                                        onDrop={() => handleColumnDrop(durum.statusID)}
                                        style={{
                                            background: '#f9fafb',
                                            borderRadius: 12,
                                            border: '1px solid #e5e7eb',
                                            minHeight: 120,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* Kolon başlığı */}
                                        <div style={{
                                            background: 'transparent',
                                            padding: '12px 14px 10px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            borderBottom: '1px solid #e5e7eb',
                                        }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#111827', fontSize: '0.8125rem' }}>
                                                <span style={{ color: col.header, display: 'flex', alignItems: 'center' }}>
                                                    {kanbanColumnIcons[idx % kanbanColumnIcons.length]}
                                                </span>
                                                {durum.statusAdi}
                                            </span>
                                            <span style={{
                                                background: 'rgba(0,0,0,0.06)', color: '#6b7280',
                                                borderRadius: 20, padding: '2px 8px', fontSize: '0.6875rem', fontWeight: 600,
                                            }}>
                                                {columnLeads.length}
                                            </span>
                                        </div>
                                        {/* Kartlar */}
                                        <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1, maxHeight: 400, overflowY: 'auto' }}>
                                            {columnLeads.length === 0 ? (
                                                <div style={{
                                                    textAlign: 'center', padding: '20px 8px',
                                                    color: '#d1d5db', fontSize: '0.78rem',
                                                    border: '1.5px dashed #e5e7eb',
                                                    borderRadius: 8,
                                                }}>
                                                    Buraya sürükleyin
                                                </div>
                                            ) : columnLeads.map((lead, cardIdx) => (
                                                <div key={lead.id} style={{ display: 'flex', flexDirection: 'column' }}>
                                                    {/* Üst insertion line */}
                                                    {dragOverDisplay?.leadId === lead.id && dragOverDisplay.position === 'before' && (
                                                        <div style={{ height: 3, borderRadius: 2, background: '#3b82f6', margin: '0 2px 4px' }} />
                                                    )}
                                                    <div
                                                        draggable
                                                        onDragStart={() => { dragLeadId.current = lead.id; }}
                                                        onDragOver={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            const pos: 'before' | 'after' = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                                                            dragOverLeadId.current = lead.id;
                                                            dragOverPosition.current = pos;
                                                            if (dragOverDisplay?.leadId !== lead.id || dragOverDisplay?.position !== pos) {
                                                                setDragOverDisplay({ leadId: lead.id, position: pos });
                                                            }
                                                        }}
                                                        onDrop={e => {
                                                            e.stopPropagation();
                                                            handleColumnDrop(durum.statusID);
                                                        }}
                                                        onClick={() => handleOpenDetail(lead)}
                                                        style={{
                                                            background: '#fff',
                                                            borderRadius: 10,
                                                            padding: '10px 14px',
                                                            border: '1px solid #e5e7eb',
                                                            cursor: 'grab',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                                            transition: 'border-color 0.15s, box-shadow 0.15s',
                                                        }}
                                                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)'; }}
                                                    >
                                                        {/* Sıralama butonları */}
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginBottom: 4 }}>
                                                            <button
                                                                onClick={e => { e.stopPropagation(); handleMoveCard(lead.id, 'up'); }}
                                                                disabled={cardIdx === 0}
                                                                style={{
                                                                    background: 'none', border: 'none', padding: '1px 5px',
                                                                    cursor: cardIdx === 0 ? 'not-allowed' : 'pointer',
                                                                    color: cardIdx === 0 ? '#d1d5db' : '#9ca3af',
                                                                    fontSize: 13, lineHeight: 1, borderRadius: 4,
                                                                }}
                                                                title="Yukarı taşı"
                                                            >▲</button>
                                                            <button
                                                                onClick={e => { e.stopPropagation(); handleMoveCard(lead.id, 'down'); }}
                                                                disabled={cardIdx === columnLeads.length - 1}
                                                                style={{
                                                                    background: 'none', border: 'none', padding: '1px 5px',
                                                                    cursor: cardIdx === columnLeads.length - 1 ? 'not-allowed' : 'pointer',
                                                                    color: cardIdx === columnLeads.length - 1 ? '#d1d5db' : '#9ca3af',
                                                                    fontSize: 13, lineHeight: 1, borderRadius: 4,
                                                                }}
                                                                title="Aşağı taşı"
                                                            >▼</button>
                                                        </div>
                                                        <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', marginBottom: 4 }}>
                                                            {lead.fullName || 'İsimsiz'}
                                                        </div>
                                                        {lead.phone && (
                                                            <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                                                                <Phone size={12} color="#9ca3af" />
                                                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{maskPhone(lead.phone)}</span>
                                                            </div>
                                                        )}
                                                        {lead.source && (
                                                            <div style={{ fontSize: '0.6875rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                                                                <Globe size={11} color="#d1d5db" />
                                                                {lead.source}
                                                            </div>
                                                        )}
                                                        {lead.assignedUserId && (
                                                            <div style={{ fontSize: '0.6875rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                                                                <User size={11} color="#9ca3af" />
                                                                {getKullaniciAdi(lead.assignedUserId)}
                                                            </div>
                                                        )}
                                                        {lead.lastActivityDate && (() => {
                                                            const daysDiff = Math.floor((Date.now() - new Date(lead.lastActivityDate).getTime()) / 86400000);
                                                            const stale = daysDiff > 3;
                                                            return (
                                                                <div style={{
                                                                    fontSize: '0.65rem',
                                                                    color: stale ? '#dc2626' : '#6b7280',
                                                                    display: 'flex', alignItems: 'center', gap: 4, marginTop: 6,
                                                                    borderTop: `1px solid ${stale ? '#fecaca' : '#f3f4f6'}`, paddingTop: 6,
                                                                }}>
                                                                    <CalendarDays size={10} color={stale ? '#dc2626' : '#9ca3af'} />
                                                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                                        {new Date(lead.lastActivityDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                                    </span>
                                                                    {stale && (
                                                                        <span title={`${daysDiff} gündür işlem yapılmadı`} style={{ display: 'inline-flex' }}>
                                                                            <AlertTriangle size={10} color="#dc2626" />
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                    {/* Alt insertion line */}
                                                    {dragOverDisplay?.leadId === lead.id && dragOverDisplay.position === 'after' && (
                                                        <div style={{ height: 3, borderRadius: 2, background: '#3b82f6', margin: '4px 2px 0' }} />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {/* Tüm Potansiyel Müşteriler */}
            <div style={{
                background: '#fff', borderRadius: '16px', padding: '24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
                    <div style={{
                        width: '40px', height: '40px', borderRadius: '10px',
                        background: '#f3f4f6', border: '1px solid #e5e7eb',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <Users size={18} color="#6b7280" />
                    </div>
                    <h4 style={{ margin: 0, fontWeight: 700, color: '#1a2340', fontSize: '1.1rem' }}>
                        Tüm Potansiyel Müşteriler
                    </h4>
                </div>

                {(() => {
                    const listeLeads = (leadDurumlar.length > 0
                        ? leads.filter(l => {
                            const durum = leadDurumlar.find(d => d.statusID === l.status);
                            return !durum || !durum.takip;
                        })
                        : leads
                    ).filter(l => {
                        if (!searchQuery) return true;
                        const q = searchQuery.toLowerCase();
                        return (l.fullName || '').toLowerCase().includes(q) || (l.phone || '').includes(q);
                    });

                    if (listeLeads.length === 0) return (
                    <div style={{
                        textAlign: 'center', padding: '48px', color: '#94a3b8',
                        background: '#f8fafc', borderRadius: '12px', border: '2px dashed #e2e8f0'
                    }}>
                        <UserPlus size={40} color="#d1d5db" style={{ marginBottom: '12px' }} />
                        <p style={{ margin: 0, fontWeight: 600, color: '#475569' }}>Henüz potansiyel müşteri yok</p>
                        <p style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>Yeni bir potansiyel müşteri ekleyerek başlayın.</p>
                    </div>
                    );

                    return (
                    <>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', fontSize: '0.85rem' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Ad Soyad</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Telefon</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Kaynak</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Sorumlu</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Durum</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listeLeads.slice((leadsPage - 1) * leadsPerPage, leadsPage * leadsPerPage).map((lead, index) => {
                                    const statusInfo = getStatusBadge(lead.status || 1);
                                    return (
                                        <tr key={lead.id}
                                            style={{ transition: 'background 0.15s', cursor: 'pointer' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                        >
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontWeight: 600, color: '#1a2340' }}>{lead.fullName || 'İsimsiz'}</td>
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>
                                                <Phone size={12} color="#cd6906" style={{ marginRight: '4px', flexShrink: 0 }} />
                                                {lead.phone ? maskPhone(lead.phone) : '-'}
                                            </td>
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>
                                                {getSourceLabel(lead.source || '')}
                                            </td>
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                                {lead.assignedUserId ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', color: '#374151', fontWeight: 600, background: '#f3f4f6', borderRadius: 6, padding: '3px 8px' }}>
                                                        <User size={12} color="#6b7280" />
                                                        {getKullaniciAdi(lead.assignedUserId)}
                                                    </span>
                                                ) : (
                                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                                <span style={{
                                                    padding: '3px 10px', borderRadius: '6px', fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    background: statusInfo.bg,
                                                    color: statusInfo.color
                                                }}>
                                                    {statusInfo.label}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                                                <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                                                    <button
                                                        onClick={() => handleOpenDetail(lead)}
                                                        style={{
                                                            background: '#e67e22',
                                                            color: '#fff', border: 'none', borderRadius: '6px',
                                                            padding: '6px 14px', fontWeight: 600, cursor: 'pointer',
                                                            fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                            transition: 'opacity 0.2s'
                                                        }}
                                                        onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                                                        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                                                    >
                                                        <Eye size={13} /> Detay
                                                    </button>
                                                    {lead.musteriCariID ? (
                                                        <span style={{
                                                            background: '#dcfce7', color: '#166534',
                                                            borderRadius: '6px', padding: '6px 10px',
                                                            fontSize: '12px', fontWeight: 600,
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            border: '1px solid #bbf7d0', whiteSpace: 'nowrap'
                                                        }}>
                                                            <CheckCircle2 size={12} /> Müşteri Eklendi
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleMusteriOlarakEkle(lead)}
                                                            disabled={convertingLeadId === lead.id}
                                                            style={{
                                                                background: convertingLeadId === lead.id ? '#e5e7eb' : '#1a2440',
                                                                color: convertingLeadId === lead.id ? '#9ca3af' : '#fff',
                                                                border: 'none', borderRadius: '6px',
                                                                padding: '6px 12px', fontWeight: 600,
                                                                cursor: convertingLeadId === lead.id ? 'not-allowed' : 'pointer',
                                                                fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                                transition: 'opacity 0.2s', whiteSpace: 'nowrap'
                                                            }}
                                                            onMouseEnter={e => { if (convertingLeadId !== lead.id) e.currentTarget.style.opacity = '0.85'; }}
                                                            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                                                        >
                                                            {convertingLeadId === lead.id
                                                                ? <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Ekleniyor...</>
                                                                : <><UserPlus size={12} /> Müşteri Ekle</>
                                                            }
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {/* Sayfalama */}
                    {listeLeads.length > leadsPerPage && (
                        <div className="lt-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '0 4px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                Toplam {listeLeads.length} kayıt, Sayfa {leadsPage} / {Math.ceil(listeLeads.length / leadsPerPage)}
                            </span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                    onClick={() => setLeadsPage(1)}
                                    disabled={leadsPage === 1}
                                    style={{
                                        padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0',
                                        background: leadsPage === 1 ? '#f8fafc' : '#fff', color: leadsPage === 1 ? '#cbd5e1' : '#475569',
                                        cursor: leadsPage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 600
                                    }}
                                >
                                    <ChevronsLeft size={14} />
                                </button>
                                <button
                                    onClick={() => setLeadsPage(p => Math.max(1, p - 1))}
                                    disabled={leadsPage === 1}
                                    style={{
                                        padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0',
                                        background: leadsPage === 1 ? '#f8fafc' : '#fff', color: leadsPage === 1 ? '#cbd5e1' : '#475569',
                                        cursor: leadsPage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 600
                                    }}
                                >
                                    <ChevronLeft size={14} />
                                </button>
                                {Array.from({ length: Math.ceil(listeLeads.length / leadsPerPage) }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === Math.ceil(listeLeads.length / leadsPerPage) || Math.abs(p - leadsPage) <= 1)
                                    .map((p, idx, arr) => (
                                        <React.Fragment key={p}>
                                            {idx > 0 && arr[idx - 1] !== p - 1 && (
                                                <span style={{ padding: '6px 4px', color: '#94a3b8', fontSize: '0.78rem' }}>...</span>
                                            )}
                                            <button
                                                onClick={() => setLeadsPage(p)}
                                                style={{
                                                    padding: '6px 12px', borderRadius: '6px',
                                                    border: p === leadsPage ? 'none' : '1px solid #e2e8f0',
                                                    background: p === leadsPage ? '#e67e22' : '#fff',
                                                    color: p === leadsPage ? '#fff' : '#475569',
                                                    cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600
                                                }}
                                            >
                                                {p}
                                            </button>
                                        </React.Fragment>
                                    ))
                                }
                                <button
                                    onClick={() => setLeadsPage(p => Math.min(Math.ceil(listeLeads.length / leadsPerPage), p + 1))}
                                    disabled={leadsPage === Math.ceil(listeLeads.length / leadsPerPage)}
                                    style={{
                                        padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0',
                                        background: leadsPage === Math.ceil(listeLeads.length / leadsPerPage) ? '#f8fafc' : '#fff',
                                        color: leadsPage === Math.ceil(listeLeads.length / leadsPerPage) ? '#cbd5e1' : '#475569',
                                        cursor: leadsPage === Math.ceil(listeLeads.length / leadsPerPage) ? 'not-allowed' : 'pointer',
                                        fontSize: '0.78rem', fontWeight: 600
                                    }}
                                >
                                    <ChevronRight size={14} />
                                </button>
                                <button
                                    onClick={() => setLeadsPage(Math.ceil(listeLeads.length / leadsPerPage))}
                                    disabled={leadsPage === Math.ceil(listeLeads.length / leadsPerPage)}
                                    style={{
                                        padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0',
                                        background: leadsPage === Math.ceil(listeLeads.length / leadsPerPage) ? '#f8fafc' : '#fff',
                                        color: leadsPage === Math.ceil(listeLeads.length / leadsPerPage) ? '#cbd5e1' : '#475569',
                                        cursor: leadsPage === Math.ceil(listeLeads.length / leadsPerPage) ? 'not-allowed' : 'pointer',
                                        fontSize: '0.78rem', fontWeight: 600
                                    }}
                                >
                                    <ChevronsRight size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                    </>
                    );
                })()}
            </div>

            {/* Not Al Modal */}
            {showModal && selectedActivity && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(4px)'
                }} onClick={() => setShowModal(false)}>
                    <div className="lt-modal-inner" style={{
                        background: '#fff', borderRadius: '20px', padding: '28px 32px',
                        width: '100%', maxWidth: '760px', maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        animation: 'fadeInUp 0.3s ease'
                    }} onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <div>
                                <h4 style={{ margin: 0, fontWeight: 700, color: '#1a2340', fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <StickyNote size={16} color="#cd6906" />
                                    {selectedActivity.lead?.fullName || 'İsimsiz'}
                                </h4>
                                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                                    {selectedActivity.lead?.phone}
                                </p>
                            </div>
                            <button onClick={() => setShowModal(false)}
                                style={{ background: '#f1f5f9', border: 'none', borderRadius: '10px', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Tab Bar */}
                        <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '20px' }}>
                            {(['Görüşme Notu', 'Müşteri Bilgileri'] as const).map(tab => (
                                <button key={tab} onClick={() => setNotAlTab(tab)}
                                    style={{ padding: '9px 18px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', color: notAlTab === tab ? '#e67e22' : '#6b7280', borderBottom: notAlTab === tab ? '2px solid #e67e22' : '2px solid transparent', marginBottom: '-2px', transition: 'color 0.15s', whiteSpace: 'nowrap' }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Tab: Görüşme Notu */}
                        {notAlTab === 'Görüşme Notu' && (
                            <>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
                                        Görüşme Notu
                                    </label>
                                    <textarea
                                        value={formNote}
                                        onChange={e => setFormNote(e.target.value)}
                                        placeholder="Görüşme detaylarını buraya yazın..."
                                        rows={4}
                                        style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '0.9rem', resize: 'vertical', outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s', boxSizing: 'border-box' }}
                                        onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                        onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                    />
                                </div>

                                {/* Tekrar Aranacak mı? */}
                                <div style={{ marginBottom: '16px', padding: '14px 16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
                                        <input type="checkbox" checked={willCallAgain}
                                            onChange={e => { setWillCallAgain(e.target.checked); if (!e.target.checked) setNextCallDate(''); }}
                                            style={{ width: '18px', height: '18px', accentColor: '#e67e22', cursor: 'pointer' }}
                                        />
                                        <span>Tekrar Aranacak mı?</span>
                                    </label>
                                    {willCallAgain && (
                                        <div style={{ marginTop: '12px' }}>
                                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#64748b', fontSize: '0.8rem' }}>Tekrar Arama Tarihi</label>
                                            <TrDateInput value={nextCallDate} onChange={e => setNextCallDate(e.target.value)} min={new Date().toISOString().split('T')[0]}
                                                style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                                onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                                onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Önceki Görüşmeler */}
                                {(loadingModalActivities || modalActivities.length > 0) && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <h5 style={{ margin: '0 0 10px', fontWeight: 700, color: '#1a2340', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <History size={14} color="#f59e0b" /> Önceki Görüşmeler
                                        </h5>
                                        {loadingModalActivities ? (
                                            <div style={{ textAlign: 'center', padding: '12px', color: '#94a3b8' }}>
                                                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                                <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>Yükleniyor...</span>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                                                {modalActivities.map(act => (
                                                    <div key={act.id} style={{ padding: '10px 14px', borderRadius: '10px', background: act.isCompleted ? '#f0fdf4' : '#fffbeb', border: act.isCompleted ? '1px solid #bbf7d0' : '1px solid #fde68a' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                            <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                <CalendarDays size={11} />
                                                                {new Date(act.activityDate).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                            <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700, background: act.isCompleted ? '#dcfce7' : '#fef3c7', color: act.isCompleted ? '#166534' : '#92400e' }}>
                                                                {act.isCompleted ? 'Tamamlandı' : 'Bekliyor'}
                                                            </span>
                                                        </div>
                                                        <p style={{ margin: 0, color: '#334155', fontSize: '0.8rem', lineHeight: '1.4' }}>
                                                            {act.note || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Not girilmemiş</span>}
                                                        </p>
                                                        {act.nextCallDate && (
                                                            <div style={{ marginTop: '4px', fontSize: '0.7rem', color: '#1a2340', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <PhoneCall size={11} /> Tekrar Arama: {new Date(act.nextCallDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Tab: Müşteri Bilgileri */}
                        {notAlTab === 'Müşteri Bilgileri' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div className="lt-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>Ad Soyad</label>
                                        <input type="text" value={modalLeadFullName} onChange={e => setModalLeadFullName(e.target.value)}
                                            style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none', fontWeight: 600, color: '#1a2340', boxSizing: 'border-box' }}
                                            onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                            onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>Telefon</label>
                                        <input type="tel" value={modalLeadPhone} onChange={e => setModalLeadPhone(e.target.value)}
                                            style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none', fontWeight: 600, color: '#1a2340', boxSizing: 'border-box' }}
                                            onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                            onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>E-posta</label>
                                    <input type="email" value={modalLeadEmail} onChange={e => setModalLeadEmail(e.target.value)} placeholder="ornek@mail.com"
                                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none', fontWeight: 500, color: '#1a2340', boxSizing: 'border-box' }}
                                        onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                        onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                    />
                                </div>
                                <div className="lt-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>Kaynak</label>
                                        <div style={{ padding: '9px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.9rem', color: '#1a2340' }}>
                                            {getSourceLabel(selectedActivity.lead?.source || '')}
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>Durum</label>
                                        <select value={modalLeadStatus} onChange={e => setModalLeadStatus(Number(e.target.value))}
                                            style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none', fontWeight: 600, color: '#1a2340', boxSizing: 'border-box', background: '#fff', cursor: 'pointer' }}
                                            onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                            onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                        >
                                            {leadDurumlar.length > 0
                                                ? leadDurumlar.filter(d => d.aktifMi).map(d => <option key={d.statusID} value={d.statusID}>{d.statusAdi}</option>)
                                                : [1,2,3,4,5].map(v => { const labels: Record<number,string> = {1:'Yeni',2:'Arandı',3:'Beklemede',4:'İlgili',5:'İlgisiz'}; return <option key={v} value={v}>{labels[v]}</option>; })
                                            }
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '4px' }}>
                                    <button onClick={handleSaveModalLead} disabled={savingModalLead}
                                        style={{ padding: '9px 22px', background: savingModalLead ? '#94a3b8' : '#1a2340', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: savingModalLead ? 'not-allowed' : 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', transition: 'opacity 0.2s' }}
                                        onMouseEnter={e => { if (!savingModalLead) e.currentTarget.style.opacity = '0.85'; }}
                                        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                                    >
                                        {savingModalLead ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Kaydediliyor...</> : <><Save size={13} /> Kaydet</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Butonlar */}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                            <button onClick={() => setShowModal(false)}
                                style={{ padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                            >
                                İptal
                            </button>
                            <button onClick={handleSaveNote} disabled={submitting}
                                style={{ padding: '10px 24px', background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #e67e22, #cf6d17)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                {submitting ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Kaydediliyor...</> : <><Save size={14} /> Kaydet</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lead Detay Modal */}
            {showDetailModal && selectedLead && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(4px)'
                }} onClick={() => setShowDetailModal(false)}>
                    <div className="lt-modal-inner" style={{
                        background: '#fff', borderRadius: '20px', padding: '32px',
                        width: '100%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
                    }} onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '44px', height: '44px', borderRadius: '12px',
                                    background: '#f3f4f6', border: '1px solid #e5e7eb',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <User size={18} color="#6b7280" />
                                </div>
                                <div>
                                    <h4 style={{ margin: 0, fontWeight: 700, color: '#1a2340', fontSize: '1.15rem' }}>
                                        {selectedLead.fullName || 'İsimsiz'}
                                    </h4>
                                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Müşteri Detayı</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                style={{
                                    background: '#f1f5f9', border: 'none', borderRadius: '10px',
                                    width: '36px', height: '36px', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#64748b', fontSize: '1rem'
                                }}
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Tab Bar */}
                        <div style={{ display: 'flex', gap: 0, marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
                            {(['Müşteri Bilgileri', 'Görüşme Geçmişi', 'Yeni Görüşme Ekle'] as const).map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setDetailTab(tab)}
                                    style={{
                                        padding: '10px 18px', border: 'none', background: 'none',
                                        cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                                        color: detailTab === tab ? '#e67e22' : '#6b7280',
                                        borderBottom: detailTab === tab ? '2px solid #e67e22' : '2px solid transparent',
                                        marginBottom: '-2px', transition: 'color 0.15s',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Tab: Müşteri Bilgileri */}
                        {detailTab === 'Müşteri Bilgileri' && (
                            <div>
                                <div style={{ padding: '18px', background: '#f8fafc', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                                    <div className="lt-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>Ad Soyad</label>
                                            <input
                                                type="text"
                                                value={editFullName}
                                                onChange={e => setEditFullName(e.target.value)}
                                                style={{
                                                    width: '100%', padding: '8px 12px', borderRadius: '8px',
                                                    border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none',
                                                    fontWeight: 600, color: '#1a2340', boxSizing: 'border-box'
                                                }}
                                                onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                                onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>Telefon</label>
                                            {(() => {
                                                const phoneIys = leadIysIzinler.find(i => i.aktifMi && i.izinDurumu === 'ONAY' && (i.iletisimTipi === 'TELEFON1' || i.iletisimTipi === 'TELEFON') && i.iletisimVerisi === editPhone);
                                                return (
                                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                        <div style={{ flex: 1, position: 'relative' }}>
                                                            <input
                                                                type="tel"
                                                                value={editPhone}
                                                                onChange={e => setEditPhone(e.target.value)}
                                                                readOnly={!!phoneIys}
                                                                style={{
                                                                    width: '100%', padding: '8px 12px', borderRadius: '8px',
                                                                    border: `1px solid ${phoneIys ? '#22c55e' : '#d1d5db'}`,
                                                                    fontSize: '0.9rem', outline: 'none',
                                                                    fontWeight: 600, color: '#1a2340', boxSizing: 'border-box',
                                                                    background: phoneIys ? '#f0fdf4' : '#fff',
                                                                    cursor: phoneIys ? 'not-allowed' : 'text'
                                                                }}
                                                                onFocus={e => { if (!phoneIys) e.currentTarget.style.borderColor = '#e67e22'; }}
                                                                onBlur={e => { if (!phoneIys) e.currentTarget.style.borderColor = '#d1d5db'; }}
                                                            />
                                                            {phoneIys && (
                                                                <span style={{
                                                                    position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                                                                    display: 'flex', alignItems: 'center', gap: '4px',
                                                                    background: '#22c55e', color: '#fff', borderRadius: '5px',
                                                                    fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', pointerEvents: 'none'
                                                                }}>
                                                                    <i className="fas fa-shield-alt" style={{ fontSize: 9 }}></i> IYS Onaylı
                                                                </span>
                                                            )}
                                                        </div>
                                                        {phoneIys ? (
                                                            <button type="button" title="IYS iznini iptal edip telefonu değiştir"
                                                                onClick={() => {
                                                                    if (!window.confirm('IYS telefon iznini iptal edip telefonu değiştirmek istiyor musunuz?')) return;
                                                                    axiosInstance.put(`/api/CariIysIzinleri/${phoneIys.iysIzinID}/pasife-al`)
                                                                        .then(() => setLeadIysIzinler(prev => prev.filter(i => i.iysIzinID !== phoneIys.iysIzinID)))
                                                                        .catch(() => alert('IYS iptal işlemi başarısız.'));
                                                                }}
                                                                style={{ padding: '7px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                                                            >
                                                                <i className="fas fa-lock-open" style={{ fontSize: 10 }}></i> IYS İptal
                                                            </button>
                                                        ) : editPhone && (
                                                            <button type="button" onClick={() => setLeadIysModal({ iletisimTipi: 'TELEFON1', iletisimVerisi: editPhone })} title="IYS Telefon İzni Al"
                                                                style={{ padding: '7px 10px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                                                            >
                                                                <i className="fas fa-shield-alt" style={{ fontSize: 10 }}></i> İzin Al
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>E-posta</label>
                                        {(() => {
                                            const emailIys = leadIysIzinler.find(i => i.aktifMi && i.izinDurumu === 'ONAY' && i.iletisimTipi === 'EPOSTA' && i.iletisimVerisi === editEmail);
                                            return (
                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    <div style={{ flex: 1, position: 'relative' }}>
                                                        <input
                                                            type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)}
                                                            placeholder="ornek@mail.com" readOnly={!!emailIys}
                                                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${emailIys ? '#22c55e' : '#d1d5db'}`, fontSize: '0.9rem', outline: 'none', fontWeight: 500, color: '#1a2340', boxSizing: 'border-box', background: emailIys ? '#f0fdf4' : '#fff', cursor: emailIys ? 'not-allowed' : 'text' }}
                                                            onFocus={e => { if (!emailIys) e.currentTarget.style.borderColor = '#e67e22'; }}
                                                            onBlur={e => { if (!emailIys) e.currentTarget.style.borderColor = '#d1d5db'; }}
                                                        />
                                                        {emailIys && (
                                                            <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '4px', background: '#22c55e', color: '#fff', borderRadius: '5px', fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', pointerEvents: 'none' }}>
                                                                <i className="fas fa-shield-alt" style={{ fontSize: 9 }}></i> IYS Onaylı
                                                            </span>
                                                        )}
                                                    </div>
                                                    {emailIys ? (
                                                        <button type="button" title="IYS iznini iptal edip e-postayı değiştir"
                                                            onClick={() => {
                                                                if (!window.confirm('IYS e-posta iznini iptal edip e-posta adresini değiştirmek istiyor musunuz?')) return;
                                                                axiosInstance.put(`/api/CariIysIzinleri/${emailIys.iysIzinID}/pasife-al`)
                                                                    .then(() => setLeadIysIzinler(prev => prev.filter(i => i.iysIzinID !== emailIys.iysIzinID)))
                                                                    .catch(() => alert('IYS iptal işlemi başarısız.'));
                                                            }}
                                                            style={{ padding: '7px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                                                        >
                                                            <i className="fas fa-lock-open" style={{ fontSize: 10 }}></i> IYS İptal
                                                        </button>
                                                    ) : editEmail && (
                                                        <button type="button" onClick={() => setLeadIysModal({ iletisimTipi: 'EPOSTA', iletisimVerisi: editEmail })} title="IYS E-posta İzni Al"
                                                            style={{ padding: '7px 10px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                                                        >
                                                            <i className="fas fa-shield-alt" style={{ fontSize: 10 }}></i> İzin Al
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    <div className="lt-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                        <div>
                                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '2px' }}>Kaynak</span>
                                            <span style={{ fontSize: '0.9rem', color: '#1a2340' }}>{getSourceLabel(selectedLead.source || '')}</span>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>Durum</label>
                                            <select value={editStatus} onChange={e => setEditStatus(Number(e.target.value))}
                                                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none', fontWeight: 600, color: '#1a2340', boxSizing: 'border-box', background: '#fff', cursor: 'pointer' }}
                                                onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                                onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                            >
                                                {leadDurumlar.length > 0
                                                    ? leadDurumlar.filter(d => d.aktifMi).map(d => <option key={d.statusID} value={d.statusID}>{d.statusAdi}</option>)
                                                    : [1,2,3,4,5].map(v => { const labels: Record<number,string> = {1:'Yeni',2:'Arandı',3:'Beklemede',4:'İlgili',5:'İlgisiz'}; return <option key={v} value={v}>{labels[v]}</option>; })
                                                }
                                            </select>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <button onClick={handleUpdateLead} disabled={savingLead}
                                            style={{ padding: '8px 20px', background: savingLead ? '#94a3b8' : '#1a2340', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: savingLead ? 'not-allowed' : 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', transition: 'opacity 0.2s' }}
                                            onMouseEnter={e => { if (!savingLead) e.currentTarget.style.opacity = '0.85'; }}
                                            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                                        >
                                            {savingLead ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Kaydediliyor...</> : <><Save size={13} /> Kaydet</>}
                                        </button>
                                    </div>
                                </div>

                                {/* IYS İzin Kayıtları */}
                                {leadIysIzinler.length > 0 && (
                                    <div style={{ padding: '14px 18px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                                        <h5 style={{ margin: '0 0 8px 0', fontWeight: 700, color: '#1a2340', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '7px' }}>
                                            <i className="fas fa-shield-alt" style={{ color: '#8b5cf6', fontSize: 13 }}></i>
                                            IYS İzin Kayıtları
                                        </h5>
                                        <IysKayitListesi izinler={leadIysIzinler} />
                                    </div>
                                )}

                                {/* Müşteri Olarak Ekle */}
                                <div style={{ padding: '16px 20px', background: selectedLead.musteriCariID ? '#f0fdf4' : '#f8fafc', borderRadius: '12px', border: selectedLead.musteriCariID ? '1px solid #bbf7d0' : '1px solid #e2e8f0', marginBottom: '16px' }}>
                                    {selectedLead.musteriCariID ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <CheckCircle2 size={20} color="#16a34a" />
                                            <div>
                                                <div style={{ fontWeight: 700, color: '#166534', fontSize: '0.9rem' }}>Müşteri Olarak Eklendi</div>
                                                <div style={{ fontSize: '0.8rem', color: '#4ade80', marginTop: '2px' }}>Bu potansiyel müşteri Cariler listesine müşteri olarak kaydedilmiştir.</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#1a2440', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <UserPlus size={15} color="#1a2440" /> Müşteri Olarak Ekle
                                                </div>
                                                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px' }}>Müşteri listesine eklenecek ve notlar kopyalanacaktır.</div>
                                            </div>
                                            <button onClick={() => handleMusteriOlarakEkle(selectedLead)} disabled={convertingLeadId === selectedLead.id}
                                                style={{ padding: '10px 22px', background: convertingLeadId === selectedLead.id ? '#e5e7eb' : '#1a2440', color: convertingLeadId === selectedLead.id ? '#9ca3af' : '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: convertingLeadId === selectedLead.id ? 'not-allowed' : 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '7px', flexShrink: 0 }}
                                            >
                                                {convertingLeadId === selectedLead.id ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Ekleniyor...</> : <><UserPlus size={14} /> Müşteri Olarak Ekle</>}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* SMS Geçmişi */}
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                        <h5 style={{ margin: 0, fontWeight: 700, color: '#1a2340', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <i className="fas fa-comment-sms" style={{ color: '#6366f1', fontSize: 14 }}></i>
                                            SMS Geçmişi
                                        </h5>
                                        <button onClick={() => setShowSmsModal(true)}
                                            style={{ padding: '6px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            <i className="fas fa-comment-sms" style={{ fontSize: 11 }}></i> SMS Gönder
                                        </button>
                                    </div>
                                    <SmsGecmisi leadID={selectedLead.id} refreshKey={smsRefreshKey} />
                                </div>
                            </div>
                        )}

                        {/* Tab: Görüşme Geçmişi */}
                        {detailTab === 'Görüşme Geçmişi' && (
                            <div>
                                {loadingActivities ? (
                                    <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                                        <Loader2 size={20} color="#cbd5e1" style={{ animation: 'spin 1s linear infinite' }} />
                                        <p style={{ margin: '8px 0 0', fontSize: '0.85rem' }}>Yükleniyor...</p>
                                    </div>
                                ) : leadActivities.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '28px', color: '#94a3b8', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #e2e8f0' }}>
                                        <MessageSquareOff size={24} color="#d1d5db" style={{ marginBottom: '8px' }} />
                                        <p style={{ margin: 0, fontSize: '0.85rem' }}>Henüz görüşme kaydı bulunmuyor.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {leadActivities.map(act => (
                                            <div key={act.id} style={{ padding: '14px 16px', borderRadius: '12px', background: act.isCompleted ? '#f0fdf4' : '#fffbeb', border: act.isCompleted ? '1px solid #bbf7d0' : '1px solid #fde68a' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                        <CalendarDays size={11} />
                                                        {new Date(act.activityDate).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                    <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 700, background: act.isCompleted ? '#dcfce7' : '#fef3c7', color: act.isCompleted ? '#166534' : '#92400e' }}>
                                                        {act.isCompleted ? 'Tamamlandı' : 'Bekliyor'}
                                                    </span>
                                                </div>
                                                <p style={{ margin: 0, color: '#334155', fontSize: '0.85rem', lineHeight: '1.4' }}>
                                                    {act.note || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Not girilmemiş</span>}
                                                </p>
                                                {act.nextCallDate && (
                                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#1a2340' }}>
                                                        <PhoneCall size={11} />
                                                        Tekrar Arama: {new Date(act.nextCallDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Tab: Yeni Görüşme Ekle */}
                        {detailTab === 'Yeni Görüşme Ekle' && (
                            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ marginBottom: '12px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '0.8rem' }}>Görüşme Notu *</label>
                                    <textarea
                                        value={newActivityNote}
                                        onChange={e => setNewActivityNote(e.target.value)}
                                        placeholder="Görüşme detaylarını yazın..."
                                        rows={4}
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.85rem', resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                        onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                        onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                    />
                                </div>
                                <div style={{ marginBottom: '12px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '0.8rem' }}>Lead Durumu</label>
                                    <select value={editStatus} onChange={e => setEditStatus(Number(e.target.value))}
                                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff', cursor: 'pointer' }}
                                        onFocus={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                        onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                    >
                                        {leadDurumlar.length > 0
                                            ? leadDurumlar.filter(d => d.aktifMi).map(d => <option key={d.statusID} value={d.statusID}>{d.statusAdi}</option>)
                                            : [1,2,3,4,5].map(v => { const labels: Record<number,string> = {1:'Yeni',2:'Arandı',3:'Beklemede',4:'İlgili',5:'İlgisiz'}; return <option key={v} value={v}>{labels[v]}</option>; })
                                        }
                                    </select>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <button onClick={handleAddActivity} disabled={savingActivity}
                                        style={{ padding: '8px 18px', background: savingActivity ? '#94a3b8' : 'linear-gradient(135deg, #e67e22, #cf6d17)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: savingActivity ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                                    >
                                        {savingActivity ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Ekleniyor...</> : <><Plus size={13} /> Görüşme Ekle</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tekrar Arama Tarihi — tüm tablarda görünür */}
                        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '2px solid #e5e7eb' }}>
                            <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', fontWeight: 700, marginBottom: '8px' }}>
                                <CalendarDays size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                                Tekrar Arama Tarihi
                            </label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <TrDateInput
                                    value={nextCallDateOnly}
                                    onChange={e => setNextCallDateOnly(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => (e.currentTarget.style.borderColor = '#e67e22')}
                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => (e.currentTarget.style.borderColor = '#d1d5db')}
                                />
                                <button onClick={handleSaveNextCallDate} disabled={savingNextCallDate || !nextCallDateOnly}
                                    style={{ padding: '8px 16px', background: (savingNextCallDate || !nextCallDateOnly) ? '#e5e7eb' : '#e67e22', color: (savingNextCallDate || !nextCallDateOnly) ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: (savingNextCallDate || !nextCallDateOnly) ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' as const }}
                                >
                                    {savingNextCallDate ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Ekleniyor...</> : <><CalendarDays size={13} /> Ekle</>}
                                </button>
                            </div>
                        </div>

                        {/* Kapat */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button onClick={() => setShowDetailModal(false)}
                                style={{ padding: '10px 24px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Yeni Lead Ekleme Modal */}
            {showAddLeadModal && (() => {
                const inp: React.CSSProperties = {
                    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
                    borderRadius: 8, fontSize: 13, color: '#111827', outline: 'none',
                    background: '#fff', boxSizing: 'border-box',
                };
                const lbl: React.CSSProperties = {
                    display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4,
                };
                return (
                    <div
                        style={{
                            position: 'fixed', inset: 0, zIndex: 9999,
                            background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
                        }}
                        onClick={() => { setShowAddLeadModal(false); setNewLeadTekrarArama(false); setNewLead({ fullName: '', phone: '', email: '', source: 'Telefon', note: '', nextCallDate: '', status: 1 }); }}
                    >
                        <div
                            style={{
                                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 576,
                                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                            }}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div style={{
                                padding: '24px 28px 16px', borderBottom: '1px solid #e5e7eb',
                                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0,
                            }}>
                                <div>
                                    <div style={{ fontSize: 18, fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <UserPlus size={18} color="#cd6906" />
                                        Yeni Potansiyel Müşteri
                                    </div>
                                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>
                                        Yeni bir potansiyel müşteri ve ilk görüşme detaylarını kaydedin.
                                    </div>
                                </div>
                                <button
                                    onClick={() => { setShowAddLeadModal(false); setNewLeadTekrarArama(false); setNewLead({ fullName: '', phone: '', email: '', source: 'Telefon', note: '', nextCallDate: '', status: 1 }); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 8, display: 'flex', alignItems: 'center' }}
                                    onMouseEnter={e => (e.currentTarget.style.color = '#4b5563')}
                                    onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Body */}
                            <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>

                                {/* Ad Soyad + Telefon */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={lbl}>Ad Soyad *</label>
                                        <input
                                            type="text"
                                            value={newLead.fullName}
                                            onChange={e => setNewLead({ ...newLead, fullName: e.target.value })}
                                            placeholder="Müşteri adı soyadı"
                                            style={inp}
                                        />
                                    </div>
                                    <div>
                                        <label style={lbl}>Telefon *</label>
                                        <input
                                            type="tel"
                                            value={newLead.phone}
                                            onChange={e => setNewLead({ ...newLead, phone: e.target.value })}
                                            placeholder="05XX XXX XX XX"
                                            style={inp}
                                        />
                                    </div>
                                </div>

                                {/* E-posta */}
                                <div>
                                    <label style={lbl}>E-posta</label>
                                    <input
                                        type="email"
                                        value={newLead.email}
                                        onChange={e => setNewLead({ ...newLead, email: e.target.value })}
                                        placeholder="ornek@mail.com"
                                        style={inp}
                                    />
                                </div>

                                {/* Kaynak + Durum */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={lbl}>Kaynak</label>
                                        <select
                                            value={newLead.source}
                                            onChange={e => setNewLead({ ...newLead, source: e.target.value })}
                                            style={inp}
                                        >
                                            <option value="Telefon">Telefon</option>
                                            <option value="Web">Web</option>
                                            <option value="Referans">Referans</option>
                                            <option value="Fuar">Fuar</option>
                                            <option value="Diğer">Diğer</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={lbl}>Durum</label>
                                        <select
                                            value={newLead.status}
                                            onChange={e => setNewLead({ ...newLead, status: Number(e.target.value) })}
                                            style={inp}
                                        >
                                            {leadDurumlar.length > 0
                                                ? leadDurumlar.filter(d => d.aktifMi).map(d => (
                                                    <option key={d.statusID} value={d.statusID}>{d.statusAdi}</option>
                                                  ))
                                                : [1,2,3,4,5].map(v => {
                                                    const labels: Record<number,string> = {1:'Yeni',2:'Arandı',3:'Beklemede',4:'İlgili',5:'İlgisiz'};
                                                    return <option key={v} value={v}>{labels[v]}</option>;
                                                  })
                                            }
                                        </select>
                                    </div>
                                </div>

                                {/* İlk Görüşme Bilgileri */}
                                <div style={{
                                    background: '#f9fafb', border: '1px solid #e5e7eb',
                                    borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#111827' }}>
                                        <PhoneCall size={14} color="#cd6906" />
                                        İlk Görüşme Bilgileri
                                    </div>
                                    <div>
                                        <label style={lbl}>Görüşme Notu</label>
                                        <textarea
                                            value={newLead.note}
                                            onChange={e => setNewLead({ ...newLead, note: e.target.value })}
                                            placeholder="Görüşme detaylarını yazın..."
                                            rows={3}
                                            style={{ ...inp, resize: 'vertical', minHeight: 100, fontFamily: 'inherit' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                                            <input
                                                type="checkbox"
                                                checked={newLeadTekrarArama}
                                                onChange={e => {
                                                    setNewLeadTekrarArama(e.target.checked);
                                                    if (!e.target.checked) setNewLead({ ...newLead, nextCallDate: '' });
                                                }}
                                                style={{ width: 16, height: 16, accentColor: '#cd6906', cursor: 'pointer' }}
                                            />
                                            Tekrar Arama
                                        </label>
                                        {newLeadTekrarArama && (
                                            <TrDateInput
                                                value={newLead.nextCallDate}
                                                onChange={e => setNewLead({ ...newLead, nextCallDate: e.target.value })}
                                                min={new Date().toISOString().split('T')[0]}
                                                style={{ ...inp, marginTop: 8 }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{
                                padding: '16px 28px', borderTop: '1px solid #e5e7eb', background: '#fff',
                                display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
                                borderRadius: '0 0 16px 16px',
                            }}>
                                <button
                                    onClick={() => { setShowAddLeadModal(false); setNewLeadTekrarArama(false); setNewLead({ fullName: '', phone: '', email: '', source: 'Telefon', note: '', nextCallDate: '', status: 1 }); }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleAddLead}
                                    disabled={addingLead}
                                    style={{
                                        padding: '8px 20px', borderRadius: 8, border: 'none',
                                        background: addingLead ? '#e5e7eb' : '#cd6906',
                                        color: addingLead ? '#9ca3af' : '#fff',
                                        fontSize: 13, fontWeight: 600, cursor: addingLead ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        boxShadow: addingLead ? 'none' : '0 1px 3px rgba(205,105,6,0.3)',
                                        transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (!addingLead) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; } }}
                                    onMouseLeave={e => { if (!addingLead) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; } }}
                                >
                                    {addingLead ? (
                                        <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Ekleniyor...</>
                                    ) : (
                                        <><Plus size={13} /> Kaydet</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Rapor Tam Sayfa ── */}
            {showRaporModal && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 100,
                    background: '#f8faff', overflowY: 'auto',
                }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 24px 60px' }}>

                        {/* Geri Dön */}
                        <button
                            onClick={() => setShowRaporModal(false)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#6b7280', fontSize: 13, fontWeight: 500, marginBottom: 20, padding: 0,
                                transition: 'color 0.15s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
                        >
                            <ArrowLeft size={16} /> Geri Dön
                        </button>

                        {/* Sayfa Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <BarChart2 size={26} color="#cd6906" />
                                <div>
                                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>
                                        Potansiyel Müşteri Raporu
                                    </h1>
                                    <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>Tarih aralığı seçerek raporu oluşturun</p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {raporReady && (
                                    <span style={{ background: '#f3f4f6', color: '#374151', padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500 }}>
                                        {raporBaslangic} – {raporBitis}
                                    </span>
                                )}
                                {raporReady && (
                                    <button onClick={exportRaporPDF} style={{
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db',
                                        borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#374151',
                                        cursor: 'pointer', transition: 'background 0.15s',
                                    }}
                                        onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                        onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    >
                                        <FileDown size={14} /> PDF İndir
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Filtre Kutusu */}
                        <div style={{
                            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
                            padding: '20px 24px', marginBottom: 28,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 16, alignItems: 'flex-end' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151', fontSize: 12 }}>Başlangıç Tarihi</label>
                                    <TrDateInput value={raporBaslangic}
                                        onChange={e => { setRaporBaslangic(e.target.value); setRaporReady(false); }}
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                        onFocus={e => (e.currentTarget.style.borderColor = '#111827')}
                                        onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151', fontSize: 12 }}>Bitiş Tarihi</label>
                                    <TrDateInput value={raporBitis}
                                        onChange={e => { setRaporBitis(e.target.value); setRaporReady(false); }}
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                        onFocus={e => (e.currentTarget.style.borderColor = '#111827')}
                                        onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                                    />
                                </div>
                                <button
                                    onClick={fetchRaporData}
                                    disabled={raporLoading}
                                    style={{
                                        height: 42, padding: '0 28px',
                                        background: raporLoading ? '#e5e7eb' : '#cd6906',
                                        color: raporLoading ? '#9ca3af' : '#fff',
                                        border: 'none', borderRadius: 8, fontWeight: 600,
                                        cursor: raporLoading ? 'not-allowed' : 'pointer', fontSize: 13,
                                        display: 'flex', alignItems: 'center', gap: 7,
                                        boxShadow: raporLoading ? 'none' : '0 1px 3px rgba(205,105,6,0.3)',
                                        whiteSpace: 'nowrap', transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (!raporLoading) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; } }}
                                    onMouseLeave={e => { if (!raporLoading) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; } }}
                                >
                                    {raporLoading
                                        ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Yükleniyor...</>
                                        : <><FileText size={14} /> Rapor Oluştur</>}
                                </button>
                            </div>
                        </div>

                        {/* Yükleniyor */}
                        {raporLoading && (
                            <div style={{ textAlign: 'center', padding: '80px', color: '#9ca3af' }}>
                                <Loader2 size={44} color="#d1d5db" style={{ marginBottom: 16, animation: 'spin 1s linear infinite' }} />
                                <p style={{ margin: 0, fontWeight: 600, color: '#6b7280', fontSize: 15 }}>Rapor hazırlanıyor...</p>
                            </div>
                        )}

                        {/* Rapor İçeriği */}
                        {raporReady && !raporLoading && (() => {
                            const {
                                filteredActivities, yeniLeadler, dailyCounts, allDays,
                                userCounts, statusCounts, tamamlanan, bekleyen
                            } = getRaporData();
                            const statusLabels: Record<number, string> = { 1: 'Yeni', 2: 'Arandı', 3: 'Beklemede', 4: 'İlgili', 5: 'İlgisiz' };
                            const statusColors = ['rgba(59,130,246,0.8)', 'rgba(245,158,11,0.8)', 'rgba(236,72,153,0.8)', 'rgba(16,185,129,0.8)', 'rgba(239,68,68,0.8)'];

                            return (
                                <>
                                {/* Özet Metrik Kartları */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 28 }}>
                                    {[
                                        { label: 'Toplam Lead', value: raporAllLeads.length, icon: <Users size={20} />, iconColor: '#cd6906', iconBg: 'rgba(205,105,6,0.1)', trend: null },
                                        { label: 'Dönemde Yeni Lead', value: yeniLeadler.length, icon: <UserPlus size={20} />, iconColor: '#10b981', iconBg: '#ecfdf5', trend: 'up' },
                                        { label: 'Toplam Görüşme', value: filteredActivities.length, icon: <PhoneCall size={20} />, iconColor: '#6366f1', iconBg: '#eff6ff', trend: null },
                                        { label: 'Tamamlanan', value: tamamlanan, icon: <CheckCircle2 size={20} />, iconColor: '#059669', iconBg: '#f0fdf4', trend: 'up' },
                                    ].map((card, i) => (
                                        <div key={i} style={{
                                            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
                                            padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                                <span style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>{card.label}</span>
                                                <div style={{ width: 36, height: 36, borderRadius: 9, background: card.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.iconColor }}>
                                                    {card.icon}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 32, fontWeight: 700, color: '#111827', lineHeight: 1 }}>{card.value}</div>
                                            {card.trend === 'up' && card.value > 0 && (
                                                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 500, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <TrendingUp size={13} /> Aktif dönem
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Grafik Satırı 1 */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '22px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', minHeight: 350 }}>
                                        <h5 style={{ margin: '0 0 18px', fontWeight: 600, color: '#111827', fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
                                            <BarChart2 size={15} color="#6366f1" /> Günlük Görüşme Sayısı
                                        </h5>
                                        <div style={{ height: 280 }}>
                                            <Bar data={{
                                                labels: allDays.map(d => { const p = d.split('-'); return `${p[2]}.${p[1]}`; }),
                                                datasets: [{ label: 'Görüşme', data: allDays.map(d => dailyCounts[d] || 0), backgroundColor: 'rgba(99,102,241,0.75)', borderRadius: 6 }]
                                            }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: '#f3f4f6' } }, x: { ticks: { maxRotation: 45, font: { size: 10 } }, grid: { display: false } } } }} />
                                        </div>
                                    </div>
                                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '22px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', minHeight: 350 }}>
                                        <h5 style={{ margin: '0 0 18px', fontWeight: 600, color: '#111827', fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
                                            <Users size={15} color="#8b5cf6" /> Kullanıcı Bazlı Görüşme
                                        </h5>
                                        {Object.keys(userCounts).length === 0 ? (
                                            <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>Bu dönemde görüşme bulunmuyor</div>
                                        ) : (
                                            <div style={{ height: 280 }}>
                                                <Bar data={{
                                                    labels: Object.keys(userCounts).map(uid => getRaporUserName(Number(uid))),
                                                    datasets: [{ label: 'Görüşme', data: Object.values(userCounts), backgroundColor: Object.keys(userCounts).map((_, i) => ['rgba(139,92,246,0.75)', 'rgba(99,102,241,0.75)', 'rgba(59,130,246,0.75)', 'rgba(16,185,129,0.75)', 'rgba(245,158,11,0.75)'][i % 5]), borderRadius: 6 }]
                                                }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: '#f3f4f6' } }, x: { ticks: { font: { size: 12 } }, grid: { display: false } } } }} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Grafik Satırı 2 */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
                                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '22px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', minHeight: 320 }}>
                                        <h5 style={{ margin: '0 0 18px', fontWeight: 600, color: '#111827', fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
                                            <CheckCircle2 size={15} color="#10b981" /> Görüşme Tamamlanma Durumu
                                        </h5>
                                        {filteredActivities.length === 0 ? (
                                            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>Bu dönemde görüşme bulunmuyor</div>
                                        ) : (
                                            <div style={{ height: 250, display: 'flex', justifyContent: 'center' }}>
                                                <Doughnut data={{ labels: ['Tamamlanan', 'Bekleyen'], datasets: [{ data: [tamamlanan, bekleyen], backgroundColor: ['rgba(16,185,129,0.85)', 'rgba(245,158,11,0.85)'], borderWidth: 3, borderColor: '#fff' }] }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { font: { size: 12 }, padding: 16 } } } }} />
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '22px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', minHeight: 320 }}>
                                        <h5 style={{ margin: '0 0 18px', fontWeight: 600, color: '#111827', fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
                                            <TrendingUp size={15} color="#cd6906" /> Lead Durum Dağılımı
                                        </h5>
                                        {Object.keys(statusCounts).length === 0 ? (
                                            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>Lead bulunamadı</div>
                                        ) : (
                                            <div style={{ height: 250, display: 'flex', justifyContent: 'center' }}>
                                                <Doughnut data={{ labels: Object.keys(statusCounts).map(s => statusLabels[Number(s)] || `Durum ${s}`), datasets: [{ data: Object.values(statusCounts), backgroundColor: statusColors.slice(0, Object.keys(statusCounts).length), borderWidth: 3, borderColor: '#fff' }] }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { font: { size: 12 }, padding: 12 } } } }} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Personel Performans İstatistikleri */}
                                {(() => {
                                    const { userCounts: uc, userTamamlanan: ut, userBekleyen: ub, userLeadCounts: ulc } = getRaporData();
                                    const ids = Array.from(new Set([...Object.keys(uc).map(Number), ...Object.keys(ulc).map(Number)]));
                                    if (ids.length === 0) return null;
                                    const maxL = Math.max(1, ...ids.map(id => ulc[id] || 0));
                                    const maxA = Math.max(1, ...ids.map(id => uc[id] || 0));
                                    const sorted = [...ids].sort((a, b) => ((ulc[b] || 0) + (uc[b] || 0)) - ((ulc[a] || 0) + (uc[a] || 0)));
                                    return (
                                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', marginBottom: 20, overflow: 'hidden' }}>
                                            <div style={{ padding: '18px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Users size={16} color="#6366f1" />
                                                <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>Personel Performans İstatistikleri</span>
                                                <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 500 }}>{ids.length} personel</span>
                                            </div>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                    <thead>
                                                        <tr style={{ background: '#f9fafb' }}>
                                                            {['Personel', 'Atanmış Lead', 'Toplam Görüşme', 'Tamamlanan', 'Bekleyen', 'Tamamlanma Oranı'].map(h => (
                                                                <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Personel' ? 'left' : 'center', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sorted.map((uid, idx) => {
                                                            const leads = ulc[uid] || 0;
                                                            const toplam = uc[uid] || 0;
                                                            const tam = ut[uid] || 0;
                                                            const bek = ub[uid] || 0;
                                                            const oran = toplam > 0 ? Math.round((tam / toplam) * 100) : 0;
                                                            const oranColor = oran >= 70 ? '#10b981' : oran >= 40 ? '#f59e0b' : '#ef4444';
                                                            return (
                                                                <tr key={uid} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 1 ? '#fafafa' : '' }}>
                                                                    <td style={{ padding: '11px 16px', fontWeight: 600, color: '#111827' }}>{getRaporUserName(uid)}</td>
                                                                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                                                                        <span style={{ background: '#1e293b', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{leads}</span>
                                                                        <div style={{ height: 4, background: '#e5e7eb', borderRadius: 4, marginTop: 4, width: 80, margin: '4px auto 0' }}>
                                                                            <div style={{ height: '100%', width: `${Math.round((leads / maxL) * 100)}%`, background: 'linear-gradient(90deg,#1a2340,#3b82f6)', borderRadius: 4 }} />
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                                                                        <span style={{ background: '#1e293b', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{toplam}</span>
                                                                        <div style={{ height: 4, background: '#e5e7eb', borderRadius: 4, marginTop: 4, width: 80, margin: '4px auto 0' }}>
                                                                            <div style={{ height: '100%', width: `${Math.round((toplam / maxA) * 100)}%`, background: 'linear-gradient(90deg,#6366f1,#8b5cf6)', borderRadius: 4 }} />
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '11px 16px', textAlign: 'center' }}><span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{tam}</span></td>
                                                                    <td style={{ padding: '11px 16px', textAlign: 'center' }}><span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{bek}</span></td>
                                                                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                                                                            <div style={{ height: 6, width: 60, background: '#e5e7eb', borderRadius: 4 }}>
                                                                                <div style={{ height: '100%', width: `${oran}%`, background: oranColor, borderRadius: 4 }} />
                                                                            </div>
                                                                            <span style={{ fontWeight: 700, fontSize: 12, color: oranColor }}>{oran}%</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Dönemde Eklenen Müşteriler Tablosu */}
                                {yeniLeadler.length > 0 && (
                                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', marginBottom: 20, overflow: 'hidden' }}>
                                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <UserPlus size={16} color="#10b981" />
                                            <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>Dönemde Eklenen Müşteriler</span>
                                            <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 500 }}>{yeniLeadler.length}</span>
                                        </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                <thead>
                                                    <tr style={{ background: '#f9fafb' }}>
                                                        {['Ad Soyad', 'Telefon', 'Kaynak', 'Durum'].map(h => (
                                                            <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {yeniLeadler.map((lead, idx) => {
                                                        const sb = getStatusBadge(lead.status || 1);
                                                        return (
                                                            <tr key={lead.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
                                                                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                                                onMouseLeave={e => (e.currentTarget.style.background = '')}
                                                            >
                                                                <td style={{ padding: '11px 20px', fontWeight: 600, color: '#111827' }}>{lead.fullName || '–'}</td>
                                                                <td style={{ padding: '11px 20px', color: '#374151' }}>{lead.phone || '–'}</td>
                                                                <td style={{ padding: '11px 20px', color: '#6b7280' }}>{lead.source || '–'}</td>
                                                                <td style={{ padding: '11px 20px' }}>
                                                                    <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: sb.bg, color: sb.color }}>{sb.label}</span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Görüşme Listesi Tablosu */}
                                {filteredActivities.length > 0 && (
                                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <PhoneCall size={16} color="#6366f1" />
                                            <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>Görüşme Listesi</span>
                                            <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 500 }}>{filteredActivities.length}</span>
                                        </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                <thead>
                                                    <tr style={{ background: '#f9fafb' }}>
                                                        {['Müşteri', 'Kullanıcı', 'Tarih', 'Not', 'Durum'].map((h, i) => (
                                                            <th key={h} style={{ padding: '10px 20px', textAlign: i === 4 ? 'center' : 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredActivities.map(act => (
                                                        <tr key={act.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
                                                            onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                                            onMouseLeave={e => (e.currentTarget.style.background = '')}
                                                        >
                                                            <td style={{ padding: '11px 20px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>{act.lead?.fullName || '–'}</td>
                                                            <td style={{ padding: '11px 20px', color: '#374151', whiteSpace: 'nowrap' }}>{getRaporUserName(act.userId)}</td>
                                                            <td style={{ padding: '11px 20px', color: '#6b7280', whiteSpace: 'nowrap' }}>{new Date(act.activityDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                                                            <td style={{ padding: '11px 20px', color: '#374151', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {act.note || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not girilmemiş</span>}
                                                            </td>
                                                            <td style={{ padding: '11px 20px', textAlign: 'center' }}>
                                                                <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: act.isCompleted ? '#dcfce7' : '#fef3c7', color: act.isCompleted ? '#166534' : '#92400e' }}>
                                                                    {act.isCompleted ? 'Tamamlandı' : 'Bekliyor'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {filteredActivities.length === 0 && yeniLeadler.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '64px', color: '#9ca3af', background: '#fff', borderRadius: 14, border: '2px dashed #e5e7eb' }}>
                                        <Search size={44} color="#d1d5db" style={{ marginBottom: 14 }} />
                                        <p style={{ margin: 0, fontWeight: 600, color: '#6b7280', fontSize: 15 }}>Seçilen dönemde veri bulunamadı</p>
                                        <p style={{ margin: '6px 0 0', fontSize: 13 }}>Farklı bir tarih aralığı deneyin.</p>
                                    </div>
                                )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
            {/* ── /Rapor Tam Sayfa ── */}

            {showSmsModal && selectedLead && (
                <SmsGonderModal
                    onClose={() => setShowSmsModal(false)}
                    onSent={() => setSmsRefreshKey(k => k + 1)}
                    telefonlar={selectedLead.phone ? [{ numara: selectedLead.phone, etiket: 'Telefon' }] : []}
                    leadID={selectedLead.id}
                    musteriAdi={selectedLead.fullName}
                />
            )}

            {leadIysModal && selectedLead && (
                <IysIzinModal
                    cariID={0}
                    leadID={selectedLead.id}
                    iletisimTipi={leadIysModal.iletisimTipi}
                    iletisimVerisi={leadIysModal.iletisimVerisi}
                    onClose={() => setLeadIysModal(null)}
                    onSuccess={() => {
                        setLeadIysModal(null);
                        axiosInstance.get(`/api/CariIysIzinleri/lead/${selectedLead.id}`)
                            .then(r => setLeadIysIzinler(r.data))
                            .catch(() => {});
                    }}
                />
            )}

        </div>
        </>
    );
};

export default LeadTakip;

